import { toStoredPersonName } from '../utils/personName';
import { useQueryClient } from '@tanstack/react-query';

import { remapAggregateCreateDimensionUnits } from '../request/dimensionUnitChoices';
import { DimensionUnitsProvider } from '../request/dimensionUnits';
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SelectedFile } from '../components/forms/FileUpload';
import { StepFirstSubsectionExpandProvider } from '../components/forms/stepFirstSubsectionExpandContext';
import { DevicesView } from '../components/locationTags/devices/DevicesView';
import {
  DevicesViewToolbarButton,
  LocationTagsControl,
} from '../components/locationTags/LocationTagsControl';
import { RecordingWarningModal } from '../components/modals/RecordingWarningModal';
import { TextIntakeModal } from '../components/modals/TextIntakeModal';
import { AudioRecordingBar } from '../components/shared/AudioRecordingBar';
import { AudioRecordingButton } from '../components/shared/AudioRecordingButton';
import {
  KeyboardAwareFormScrollView,
  KEYBOARD_BOTTOM_OFFSET,
} from '../components/shared/KeyboardAwareFormScrollView';
import { KeyboardDismissToolbar } from '../components/shared/KeyboardDismissToolbar';
import { PasteTextButton } from '../components/shared/PasteTextButton';
import {
  RequestToolsMenu,
  type RequestToolsMenuItem,
} from '../components/shared/RequestToolsMenu';
import { UploadProgressBar } from '../components/shared/UploadProgressBar';
import { RequestDeliverablesStep } from '../components/steps/RequestDeliverablesStep';
import { SubHeader } from '../components/SubHeader';
import { featureFlags } from '../config/featureFlags';
import { getDefaultEnabledStepIdsForNewRequest } from '../config/requestFormStepRegistry';
import { COOLING_SYSTEM_EDIT_SAVE_DRAFT_LABEL } from '../constants/coolingSystemConfigurationsSubsectionCopy';
import { LARGE_UPS_EDIT_SAVE_DRAFT_LABEL } from '../constants/largeUpsConfigurationsSubsectionCopy';
import { LIQUID_COOLING_CDU_EDIT_SAVE_DRAFT_LABEL } from '../constants/liquidCoolingCduConfigurationsSubsectionCopy';
import { POWER_DISTRIBUTION_EDIT_SAVE_DRAFT_LABEL } from '../constants/powerDistributionConfigurationsSubsectionCopy';
import {
  RACK_DISCARD_UNSAVED_DISCARD_LABEL,
  RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  RACK_DISCARD_UNSAVED_MESSAGE,
  RACK_DISCARD_UNSAVED_TITLE,
  RACK_EDIT_SAVE_DRAFT_LABEL,
} from '../constants/rackConfigurationsSubsectionCopy';
import {
  getRequestFormNativeFooterPaddingBottom,
  REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT,
  REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING,
} from '../constants/requestFormLayout';
import { SECTION_IDS } from '../constants/sectionIds';
import { SMALL_UPS_EDIT_SAVE_DRAFT_LABEL } from '../constants/smallUpsConfigurationsSubsectionCopy';
import { useAuth } from '../contexts/AuthContext';
import { useFormEditing } from '../contexts/FormEditingContext';
import {
  dismissActiveRichTextKeyboard,
  FormKeyboardScrollProvider,
} from '../contexts/FormKeyboardScrollContext';
import { useOptionalLocationTagsDraft } from '../contexts/LocationTagsDraftContext';
import { useRequestFrameToolbar } from '../contexts/RequestFrameContext';
import {
  useRegisterRequestLeaveGuard,
  useRegisterRequestStepChangeGuard,
} from '../contexts/RequestLeaveGuardContext';
import { useRequestSaveTransition } from '../contexts/RequestSaveTransitionContext';
import {
  useRegisterRequestScope,
  useRequestScope,
  type RequestScopeRegistration,
  type RequestScopeSnapshot,
} from '../contexts/RequestScopeContext';
import { RequestStageProvider } from '../contexts/RequestStageContext';
import { useSidebarStepMetrics } from '../contexts/SidebarStepMetricsContext';
import { debugAttachmentScope } from '../dev/attachmentScopeDebug';
import {
  checkScopeLoop,
  logScopeEffectDeps,
  registrationFingerprint,
  scopeLoopLog,
  useScopeLoopRenderCounter,
} from '../dev/scopeLoopDebug';
import { useCreateRequestWithImport } from '../hooks/queries/useCreateRequestWithImport';
import { locationTagsKeys } from '../hooks/queries/useLocationTags';
import {
  useOfflineCreateRequest,
  useOfflineUpdateRequest,
  offlineRequestsKeys,
} from '../hooks/queries/useOfflineRequestsQuery';
import { rackConfigurationsKeys } from '../hooks/queries/useRackConfigurations';
import { requestsKeys } from '../hooks/queries/useRequestsQuery';
import { useButtonPermissions } from '../hooks/useButtonPermissions';
import { useCompactRequestFooter } from '../hooks/useCompactRequestFooter';
import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import { useFormPermissions } from '../hooks/useFormPermissions';
import { useInlineImageUploadFooterProgress } from '../hooks/useInlineImageUploadFooterProgress';
import { useInlineImageUploadsInFlight } from '../hooks/useInlineImageUploadsInFlight';
import { useOfflineFileRestriction } from '../hooks/useOfflineFileRestriction';
import { useOfflineToast } from '../hooks/useOfflineToast';
import { useReconcileRepeatableSectionsAfterSync } from '../hooks/useReconcileRepeatableSectionsAfterSync';
import {
  useBeforeUnloadPersist,
  useDebouncedCallback,
} from '../hooks/useRequestDraftPersistence';
import { useResponsive } from '../hooks/useResponsive';
import { useStepVisibility } from '../hooks/useStepVisibility';
import { useUserProfile } from '../hooks/useUserProfile';
import { ApiError } from '../services/api';
import {
  extractCreateRequestRepeatableDrafts,
  flushCreateRequestRepeatableDrafts,
  persistCreateRequestRepeatableDraftsAfterSave,
  primeCreateRequestRepeatableDraftsInCache,
  applyAggregateCreateIdMap,
  aggregateCreateDimensionUnitsSegment,
  buildAggregateCreateFamilySegments,
  invalidateRepeatableFamilyQueries,
} from '../services/createRequestRepeatableSectionFlush';
import {
  applyDeliverableComment,
  applyDeliverableEntries,
  NEW_REQUEST_DELIVERABLES_ID,
  saveDeliverables,
  withoutDeliverableType,
} from '../services/deliverablesService';
import {
  fileUploadService,
  FileUploadProgress,
} from '../services/fileUploadService';
import {
  rewritePreviewSrcsInRecord,
  StagedInlineImageFlushError,
} from '../services/flushOfflineInlineImageQueue';
import {
  flushDraftLocationTagsWithWarning,
  remapDraftLocationTagIds,
} from '../services/locationTagDraftFlush';
import { offlineCache } from '../services/offlineCache';
import { listOfflineInlineImages } from '../services/offlineInlineImageQueue';
import { offlineRequestsService } from '../services/offlineRequestsService';
import { flushDraftRackConfigurations } from '../services/rackConfigurationDraft';
import {
  clearCreateRequestDraftSnapshot,
  hasCreateDraftContent,
  hasCreateDraftTags,
  readCreateRequestDraftSnapshot,
  writeCreateRequestDraftSnapshot,
  writeCreateRequestDraftSnapshotSync,
  REQUEST_DRAFT_RESTORED_MESSAGE,
  isBrowserReloadNavigation,
  isFreshCreateRequestQuery,
} from '../services/requestDraftSnapshot';
import { RequestsService } from '../services/requestsService';
import { transcriptionService } from '../services/transcriptionService';
import { colors, layout, spacing } from '../theme';
import { CreateRequestData } from '../types';
import { normalizeStage, RequestStage } from '../types/stages';
import { markAttachmentsUploadedForRequest } from '../utils/attachmentUploadHint';
import { generateAudioFilename, renameAudioFile } from '../utils/audioFilename';
import {
  normalizeRequestFormStepId,
  LEGACY_COMBINED_SITE_STEP_ID,
} from '../utils/formStepIds';
import { buildRequestFormSteps } from '../utils/formSteps';
import {
  validateRequiredFields,
  formatValidationErrors,
  FormValidationError,
} from '../utils/formValidation';
import { resolveCreateRequestStageRequestId } from '../utils/locationTagRequestId';
import { normalizeRequestNumericFields } from '../utils/normalizeRequestNumericFields';
import { clearOpportunitySubsectionExpandStateForCreate } from '../utils/opportunitySubsectionExpandState';
import {
  commitRepeatableSaveTarget,
  resolveRepeatableSaveTarget,
  resolveRequestFooterSaveLabel,
} from '../utils/requestFooterSaveContext';
import { showsInternalStageDetail } from '../utils/requestorBoundary';
import {
  diffRemovedOptionalStepIds,
  stripFormFieldsForRemovedSteps,
} from '../utils/requestScopeChange';
import { resolveEnabledStepIds } from '../utils/resolveRequestFormSteps';
import { waitForInlineImagePipelineIdle } from '../utils/richContent/inlineAttachmentName';
import { prepareTextAreaFieldsForTransport } from '../utils/richContent/sanitizeTextArea';
import { applyScopeRemovalSideEffects } from '../utils/scopeRemovalSideEffects';
import { shouldShowStepInSidebarNav } from '../utils/sidebarScopeStepVisibility';
import {
  buildTeamMembersFromForm,
  hasTeamMemberChanges,
  saveTeamMembersFromForm,
} from '../utils/teamMemberUtils';
import { showToast } from '../utils/toast';
import {
  ChevronLeft,
  ChevronRight,
  Mic,
  TextIcon,
} from './../components/icons';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { RequestFormStepId } from '../config/requestFormStepRegistry';
import type { CoolingSystemConfigurationsSaveHandle } from '../request/steps/cooling/subsections/coolingSystemConfigurationsSaveHandle';
import type { LargeUpsConfigurationsSaveHandle } from '../request/steps/largeups/subsections/largeUpsConfigurationsSaveHandle';
import type { LiquidCoolingCduConfigurationsSaveHandle } from '../request/steps/liquidcooling/subsections/liquidCoolingCduConfigurationsSaveHandle';
import type { OpportunityFormFlushHandle } from '../request/steps/opportunityinformation/subsections/ProjectInformation';
import type { PowerDistributionConfigurationsSaveHandle } from '../request/steps/powerdistribution/subsections/powerDistributionConfigurationsSaveHandle';
import type { RackConfigurationsSaveHandle } from '../request/steps/rackpower/subsections/rackConfigurationsSaveHandle';
import type { SmallUpsConfigurationsSaveHandle } from '../request/steps/smallups/subsections/smallUpsConfigurationsSaveHandle';
import type { AudioFile } from '../services/audio';
import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { FacilityPowerInstance } from '../types/facilityPowerInstance';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type {
  RequestDeliverable,
  RequestDeliverableEntry,
  RequestDeliverableType,
} from '../types/requestDeliverables';
import type { SmallUpsConfiguration } from '../types/smallUps';
import type { DeviceSourceUnit, DeviceStepId } from '../utils/deviceRowMapper';

const isWeb = Platform.OS === 'web';

/**
 * The Deliverables section's step id. Deliberately **not** a member of
 * `REQUEST_FORM_STEP_REGISTRY` / `STEP_COMPONENTS` / `stepSectionMap` — see
 * `RequestDeliverablesStep` for why — so every "is this step in scope?" check in this
 * screen has to exempt it explicitly.
 */
const DELIVERABLES_STEP_ID = 'deliverables';

/**
 * The deliverables held on the form, normalised. Module scope so the three `useCallback`s
 * below do not have to carry it as a dependency.
 */
const heldDeliverables = (form: {
  deliverables?: unknown;
}): RequestDeliverable[] =>
  Array.isArray(form.deliverables)
    ? (form.deliverables as RequestDeliverable[])
    : [];

export interface CreateRequestProps {
  navigate: (path: string) => void;
  sidebarCollapsed: boolean;
  currentStep?: string;
  onStepChange?: (stepId: string) => void;
  recordingState: 'idle' | 'recording' | 'paused';
  elapsedTime: number;
  onStartRecording: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onAcceptRecording: () => void;
  onDiscardRecording: () => void;
  recordedAudioFile?: AudioFile | null;
  /** Main-bundle nav scope for lazy-loaded step components (#1330). */
  navScopeSnapshot?: RequestScopeSnapshot | null;
  /** Main-bundle scope registration bridge for lazy-loaded Create Request (#1330). */
  onScopeRegistrationChange?: (
    registration: RequestScopeRegistration | null,
  ) => void;
}

export const CreateRequest: React.FC<CreateRequestProps> = ({
  navigate,
  sidebarCollapsed: _sidebarCollapsed,
  currentStep = 'opportunity',
  onStepChange,
  recordingState,
  elapsedTime,
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onAcceptRecording,
  onDiscardRecording,
  recordedAudioFile,
  navScopeSnapshot = null,
  onScopeRegistrationChange,
}: CreateRequestProps) => {
  const requestSaveTransition = useRequestSaveTransition();
  useScopeLoopRenderCounter('CreateRequest');
  const { canCreateRequest, user } = useFormPermissions();
  const { isLoading: isAuthLoading } = useAuth();
  const { profile } = useUserProfile();
  const queryClient = useQueryClient();
  const { isOnline } = useOfflineToast();
  const { shouldDisableUploads } = useOfflineFileRestriction();

  // Refetch repeatable-section lists once an offline sync flushes their queued
  // ops, so optimistic offline copies reconcile with the synced server copies
  // without a manual refresh (fixes duplicate-until-refresh on reconnect).
  useReconcileRepeatableSectionsAfterSync();
  const createRequestMutation = useOfflineCreateRequest();
  const updateRequestMutation = useOfflineUpdateRequest();
  const requestScope = useRequestScope();
  const locationTagsDraft = useOptionalLocationTagsDraft();
  const requestScopeRef = useRef(requestScope);
  requestScopeRef.current = requestScope;
  const createWithImportMutation = useCreateRequestWithImport();
  const { isSmallScreen } = useResponsive();
  const scrollViewRef = useRef<ScrollView>(null);
  const { setEditingScreen } = useFormEditing();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  const nativeFooterPaddingBottom = !isWeb
    ? getRequestFormNativeFooterPaddingBottom(safeAreaBottomInset)
    : undefined;

  const stepId = normalizeRequestFormStepId(currentStep);

  const buildOpenedRequestPath = useCallback(
    (requestId: string | number, extra?: Record<string, string>) => {
      const params = new URLSearchParams({
        id: String(requestId),
        step: stepId,
      });
      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          params.set(key, value);
        });
      }
      return `/request-detail?${params.toString()}`;
    },
    [stepId],
  );

  useEffect(() => {
    if (!onStepChange) return;
    if (
      currentStep === LEGACY_COMBINED_SITE_STEP_ID ||
      currentStep === 'site-walk'
    ) {
      onStepChange('opportunity');
      return;
    }
    if (currentStep === 'racks') {
      onStepChange('rack-power');
      return;
    }
    if (currentStep === 'customer-it') {
      onStepChange('monitoring');
      return;
    }
    if (currentStep === 'requestor') {
      onStepChange('opportunity');
      return;
    }
  }, [currentStep, onStepChange]);

  // Mark form as being edited when component mounts
  // This prevents automatic query invalidation from disrupting form data entry
  useEffect(() => {
    clearOpportunitySubsectionExpandStateForCreate();
    setEditingScreen('CreateRequest');
    return () => {
      // Clear editing state when component unmounts
      setEditingScreen(null);
    };
  }, [setEditingScreen]);

  // Auto-populate requestorRole and requestorPhone from user profile when it loads
  // Note: formData fields intentionally not in deps - we only want to
  // populate once when profile loads, not re-run when user edits the fields
  useEffect(() => {
    if (profile?.userRole && !formData.requestorRole) {
      setFormData(prev => ({
        ...prev,
        requestorRole: profile.userRole,
      }));
    }

    const defaultPhone = profile?.phone || user?.phone;
    if (defaultPhone && !formData.requestorPhone) {
      setFormData(prev => ({
        ...prev,
        requestorPhone: defaultPhone,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time fill from profile; not when user edits requestor fields
  }, [profile?.userRole, profile?.phone, user?.phone]);

  // Get step visibility based on permissions for new requests (DRAFT stage)
  // Pass 'new-request' as requestId to ensure hook detects switch from existing requests
  const { stepVisibility, isLoading: isLoadingStepVisibility } =
    useStepVisibility('DRAFT', 'new-request');

  // Audio transcription state
  const [showRecordingWarning, setShowRecordingWarning] = useState(false);

  // Text intake (Paste text) — creates the request from pasted text, same
  // outcome as the in-wizard audio recording flow. Gated on the DRAFT
  // Button - Text Intake matrix cell (same cell as ViewRequests' entry).
  const [textIntakeModalVisible, setTextIntakeModalVisible] = useState(false);
  const [textIntakeSubmitting, setTextIntakeSubmitting] = useState(false);
  const draftButtonPermissions = useButtonPermissions('DRAFT');
  const rackSaveHandleRef = useRef<RackConfigurationsSaveHandle | null>(null);
  const instanceSaveHandleRef = useRef<RepeatableInstanceSaveHandle | null>(
    null,
  );
  const largeUpsSaveHandleRef = useRef<LargeUpsConfigurationsSaveHandle | null>(
    null,
  );
  const powerDistributionSaveHandleRef =
    useRef<PowerDistributionConfigurationsSaveHandle | null>(null);
  const smallUpsSaveHandleRef = useRef<SmallUpsConfigurationsSaveHandle | null>(
    null,
  );
  const coolingSystemSaveHandleRef =
    useRef<CoolingSystemConfigurationsSaveHandle | null>(null);
  const liquidCoolingCduSaveHandleRef =
    useRef<LiquidCoolingCduConfigurationsSaveHandle | null>(null);
  const opportunityFormFlushRef = useRef<OpportunityFormFlushHandle | null>(
    null,
  );
  /** After valid Save Request — stop rewriting local draft while navigation runs. */
  const skipCreateDraftPersistRef = useRef(false);
  /** Programmatic navigation to opened request after successful create save. */
  const allowLeaveAfterCreateSaveRef = useRef(false);
  const registerOpportunityFormFlush = useCallback(
    (handle: OpportunityFormFlushHandle | null) => {
      opportunityFormFlushRef.current = handle;
    },
    [],
  );
  const [rackEditorOpen, setRackEditorOpen] = useState(false);
  const [instanceEditorFooterLabel, setInstanceEditorFooterLabel] = useState<
    string | null
  >(null);
  const [largeUpsEditorOpen, setLargeUpsEditorOpen] = useState(false);
  const [powerDistributionEditorOpen, setPowerDistributionEditorOpen] =
    useState(false);
  const [smallUpsEditorOpen, setSmallUpsEditorOpen] = useState(false);
  const [coolingSystemEditorOpen, setCoolingSystemEditorOpen] = useState(false);
  const [liquidCoolingCduEditorOpen, setLiquidCoolingCduEditorOpen] =
    useState(false);
  const registerRackSaveHandle = useCallback(
    (handle: RackConfigurationsSaveHandle | null) => {
      rackSaveHandleRef.current = handle;
    },
    [],
  );
  const registerInstanceSaveHandle = useCallback(
    (handle: RepeatableInstanceSaveHandle | null) => {
      instanceSaveHandleRef.current = handle;
    },
    [],
  );
  const handleInstanceEditorActiveChange = useCallback(
    (active: boolean, footerSaveLabel?: string) => {
      setInstanceEditorFooterLabel(
        active && footerSaveLabel ? footerSaveLabel : null,
      );
    },
    [],
  );
  const registerLargeUpsSaveHandle = useCallback(
    (handle: LargeUpsConfigurationsSaveHandle | null) => {
      largeUpsSaveHandleRef.current = handle;
    },
    [],
  );
  const registerPowerDistributionSaveHandle = useCallback(
    (handle: PowerDistributionConfigurationsSaveHandle | null) => {
      powerDistributionSaveHandleRef.current = handle;
    },
    [],
  );
  const registerSmallUpsSaveHandle = useCallback(
    (handle: SmallUpsConfigurationsSaveHandle | null) => {
      smallUpsSaveHandleRef.current = handle;
    },
    [],
  );
  const registerCoolingSystemSaveHandle = useCallback(
    (handle: CoolingSystemConfigurationsSaveHandle | null) => {
      coolingSystemSaveHandleRef.current = handle;
    },
    [],
  );
  const registerLiquidCoolingCduSaveHandle = useCallback(
    (handle: LiquidCoolingCduConfigurationsSaveHandle | null) => {
      liquidCoolingCduSaveHandleRef.current = handle;
    },
    [],
  );
  const handleRackEditorActiveChange = useCallback((active: boolean) => {
    setRackEditorOpen(active);
  }, []);
  const handleLargeUpsEditorActiveChange = useCallback((active: boolean) => {
    setLargeUpsEditorOpen(active);
  }, []);
  const handlePowerDistributionEditorActiveChange = useCallback(
    (active: boolean) => {
      setPowerDistributionEditorOpen(active);
    },
    [],
  );
  const handleSmallUpsEditorActiveChange = useCallback((active: boolean) => {
    setSmallUpsEditorOpen(active);
  }, []);
  const handleCoolingSystemEditorActiveChange = useCallback(
    (active: boolean) => {
      setCoolingSystemEditorOpen(active);
    },
    [],
  );
  const handleLiquidCoolingCduEditorActiveChange = useCallback(
    (active: boolean) => {
      setLiquidCoolingCduEditorOpen(active);
    },
    [],
  );

  const repeatableSaveTarget = resolveRepeatableSaveTarget(stepId, {
    facilityPowerLabel: instanceEditorFooterLabel,
    rackOpen: rackEditorOpen,
    smallUpsOpen: smallUpsEditorOpen,
    largeUpsOpen: largeUpsEditorOpen,
    powerDistributionOpen: powerDistributionEditorOpen,
    coolingOpen: coolingSystemEditorOpen,
    liquidCoolingOpen: liquidCoolingCduEditorOpen,
  });
  const footerSaveLabel = resolveRequestFooterSaveLabel(
    repeatableSaveTarget,
    instanceEditorFooterLabel,
    {
      rack: RACK_EDIT_SAVE_DRAFT_LABEL,
      'small-ups': SMALL_UPS_EDIT_SAVE_DRAFT_LABEL,
      'large-ups': LARGE_UPS_EDIT_SAVE_DRAFT_LABEL,
      'power-distribution': POWER_DISTRIBUTION_EDIT_SAVE_DRAFT_LABEL,
      cooling: COOLING_SYSTEM_EDIT_SAVE_DRAFT_LABEL,
      'liquid-cooling': LIQUID_COOLING_CDU_EDIT_SAVE_DRAFT_LABEL,
    },
    'Save',
  );

  const { confirm, confirmationDialog } = useConfirmationDialog();

  const { setFormValues, setShowFullDetails, setRequestStage, setRequestId } =
    useSidebarStepMetrics();

  const [formData, setFormData] = useState<any>({
    // Basic Information - auto-populate from logged-in user
    uom: 'Imperial',
    region: '',
    // `user.name` is "Last, First" under Okta but "First Last" under the dev
    // auth bypass; the stored form is always "Last, First", which is what the
    // server's own read transform returns (#1507 — see `toStoredPersonName`).
    requestorName: toStoredPersonName(user?.name),
    requestorRole: '',
    requestorEmail: user?.email || '',
    requestorPhone: '',
    customerName: '',
    oppProjectName: '',
    requestType: '',
    stage: 'Initial',
    description: '',
    additionalDetails: '',
    enabledStepIds: getDefaultEnabledStepIdsForNewRequest(),

    // Opportunity fields - using correct database field names
    dealRegistrationNumber: '',
    oppDealOpportunityId: '',
    dealLinked: 'false',
    autoAddedSponsorEmail: '',
    oppOpportunityId: '',
    oppProjectDescription: '',
    oppProjectType: '',
    oppProjectScope: [] as string[],
    oppDeploymentTimeline: '',
    oppApproximateKw: '',
    oppBudget: '',
    oppQuoteNumber: '',
    oppSiteAddress: '',
    oppIsIntegratedSolution: '',
    oppPreferredAssessmentDate: '',
    oppWorkloadType: '',
    oppTeamNotes: '',
    oppSiteAssessmentRequired: '',
    oppSiteWalkNotes: '',
    bomInternalRouting: '',
    requestForInformation: undefined as boolean | undefined,
    configTeamInstructions: '',

    // Site Dimensions fields - using correct database field names
    siteDataCenterFlooring: '',
    siteRaisedFloorHeight: '',
    siteWeightSupportOfFloor: '',
    siteCeilingHeight: '',
    siteDroppedCeilingHeight: '',
    sitePlenumHeight: '',
    siteSlabToDroppedCeilingHeight: '',
    siteRoomLengthFtIn: '',
    siteRoomWidthSqftSqm: '',
    siteAdditionalSizeInformation: '',
    siteAdditionalSiteInformation: '',

    // Racks fields - using correct database field names
    rackDimensionsLength: '',
    rackDimensionsHeight: '',
    rackDimensionsWidth: '',
    rackSeismicRated: undefined as boolean | undefined,
    rackRequiresLiquidToChip: undefined as boolean | undefined,
    rackAdditionalRackInformation: '',
    rackConfigurations: [] as RackConfiguration[],

    // Facility Power Readiness fields - using correct database field names
    powerMainNominalLvVoltage: '',
    powerAnticipatedFinalMwSize: '',
    powerTotalDataCenterItLoadKw: '',
    powerTotalAvailablePowerKw: '',
    powerAverageItLoadPerRackKw: '',
    powerMaximumItLoadPerRackKw: '',
    powerNumberAndTypeInputPerRack: '',
    powerExistingSiteProtectedByLargeKwUps: '',
    powerModelOfExistingUps: '',
    powerLevelOfRedundancy: '',
    powerRequiredRedundancyForUps: '',
    powerBatteryChemistry: '',
    powerBatteryRuntimeRequiredMin: '',
    powerSourceLocation: '',
    powerDistributionVoltageToLoad: '',
    powerOverheadBuswayOrUnderFloor: '',
    powerBuswayAmperage: '',
    powerBuswayLengthFt: '',
    powerFacilityDescription: '',
    powerMainNominalAmps: '',
    powerLargeUpsDescription: '',
    powerFireSuppressionNeeds: '',
    powerUpsOutputVoltage: '',
    powerTapOfBoxRequirements: '',
    powerAdditionalRackPowerInfo: '',

    // Rack Power fields
    rackPowerUom: '',
    rackPduMountingType: '',
    rackPduCountPerRack: '',
    rackPduOutletSwitchingMonitored: '',
    rackPduOutletTypesQuantity: '',
    rackPduPowerType: '',
    rackPduMaxKwPerRack: '',
    rackPduAdditionalInfo: '',

    // Small UPS fields
    smallUpsUom: '',
    smallUpsManufacturer: '',
    smallUpsModel: '',
    powerSmallUpsDescription: '',
    smallUpsSerialNumber: '',
    powerTypesOfUps: '',
    smallUpsInputVoltage: '',
    smallUpsInputPlug: '',
    powerInputPowerPhase: '',
    smallUpsCapacity: '',
    smallUpsLoadPercentage: '',
    powerRuntime: '',
    smallUpsBatteryCabinetQty: '',
    smallUpsMounting: '',
    smallUpsOutlets: '',
    smallUpsBackupGenerator: undefined as boolean | undefined,
    powerSmallUpsAdditionalInfo: '',

    facilityPowerInstances: [] as FacilityPowerInstance[],
    smallUpsConfigurations: [] as SmallUpsConfiguration[],
    coolingSystemConfigurations: [] as CoolingSystemConfiguration[],
    liquidCoolingCduConfigurations: [] as LiquidCoolingCduConfiguration[],
    largeUpsConfigurations: [] as LargeUpsConfiguration[],
    powerDistributionConfigurations: [] as PowerDistributionConfiguration[],

    // Cooling Readiness fields
    coolingIsExistingSystemOnsite: '',
    coolingIsChilledWaterAvailable: '',
    coolingTotalAvailableCapacityKw: '',
    coolingAdditionalReadinessInfo: '',
    coolingRequiredRedundancyForSystem: '',
    coolingTempRisePrefThroughItEquipment: '',
    coolingDescribeApproach: '',
    coolingRearDoorHexDesired: undefined as boolean | undefined,
    coolingDescribeRearDoorLoop: '',

    // Liquid Cooling fields
    lcDescription: '',
    lcLiquidToChipDesired: undefined as boolean | undefined,
    lcCduType: '',
    lcVertivSuppliesRowManifolds: undefined as boolean | undefined,
    lcVertivSuppliesRackManifolds: undefined as boolean | undefined,
    lcCduRedundancy: '',
    lcLeakDetectionApproach: '',
    lcTotalLiquidCoolingThermalLoadPerSfn: '',
    lcNumberOfLiquidCooledRacksToBeServed: '',
    lcNumberOfAirCooledRacks: '',
    lcLiquidCoolingVsPeakRackItLoad: '',
    lcTotalItLoadPerDataHall: '',
    lcRedundancyStrategy: '',
    lcEthyleneGlycolPct: '',
    lcTps: '',
    lcTpr: '',
    lcConnectionTypePrimary: '',
    lcPrimaryFlowValve: '',
    lcCoolant: '',
    lcTss: '',
    lcTsr: '',
    lcFlowrateRequirement: '',
    lcFiltration: '',
    lcMaximumPressureRating: '',
    lcRackPressureDrop: '',
    lcExternalStaticPressureRequirement: '',
    lcConnectionTypeSecondary: '',
    lcSecondaryFluidNetwork: '',
    lcAdditionalInformationSfn: '',
    lcDatahallCeilingHeight: '',
    lcDatahallFloorType: '',
    lcDatahallRaisedFloorDepth: '',
    lcRackArrangementWithinTheDatahall: '',
    lcCduArrangementWithinTheDatahall: '',
    lcCduPrimaryPipingConnections: '',
    lcCduSecondaryPipingConnections: '',
    lcTcsFlowPathwayDefinition: '',
    lcTcsRoutingSpecialRequirements: '',
    lcTcsArrangementType: '',
    lcTcsRoutingLocation: '',
    lcTcsRackControlType: '',
    lcTcsRackConnectionSize: '',
    lcTcsRackConnectionType: '',
    lcTcsLoopPressureMaximumRating: '',
    lcTcsLoopPipeDiameter: '',
    lcRequiredTcsCoolantFlowratePerRack: '',
    lcAdditionalInformationTcs: '',

    // Monitoring Management fields
    monitoringManagementUom: '',
    mgmtDescribeMonitoringControlSoftwareNeeds: '',
    mgmtAdditionalRemoteMgmtInfo: '',

    // Supporting Documents fields
    supportingDocumentsUom: '',
    supportingDocumentsCaptions: '',

    // Team member selections
    vertivSponsors: [],
    vertivContributors: [],
    partnerContributors: [],
    useDefaultSponsors: false, // ✅ PHASE 2 FIX: Track Default Sponsor checkbox state
    // Feedback fields
    fb_rating: undefined, // Start with no rating selected
    fb_comments: '',
    // Additional fields for other steps will be added here
  });

  const [isManualSaving, setIsManualSaving] = useState(false);
  const inlineImageUploadsInFlight = useInlineImageUploadsInFlight();
  useEffect(() => {
    if (isWeb) {
      return;
    }
    requestSaveTransition.setSaving(isManualSaving);
  }, [isManualSaving, requestSaveTransition]);
  const clearManualSaveState = useCallback(() => {
    setIsManualSaving(false);
    if (isWeb) {
      requestSaveTransition.setSaving(false);
      setUploadProgress(null);
    }
  }, [requestSaveTransition]);
  const [validationErrors, setValidationErrors] = useState<
    FormValidationError[]
  >([]);
  const createDraftHydratedRef = useRef(false);
  /** Bumps after #1114 draft hydrate so Opportunity RHF re-seeds from parent formData. */
  const [createDraftSyncRevision, setCreateDraftSyncRevision] = useState(0);
  const [createDraftHydrateComplete, setCreateDraftHydrateComplete] =
    useState(false);
  /** Bumps after Save Request validation fails — Opportunity RHF inline errors (#1116). */
  const [rhfValidationRevision, setRhfValidationRevision] = useState(0);

  const formSteps = useMemo(
    () => buildRequestFormSteps(formData.enabledStepIds),
    [formData.enabledStepIds],
  );

  useEffect(() => {
    if (
      formSteps.length > 0 &&
      // The Deliverables section lives outside the step registry (see
      // `RequestDeliverablesStep`), so without this exemption arriving on it bounces
      // straight back to Opportunity.
      stepId !== DELIVERABLES_STEP_ID &&
      stepId !== 'location-view' &&
      !formSteps.some(step => step.id === stepId) &&
      onStepChange
    ) {
      onStepChange(formSteps[0].id);
    }
  }, [formSteps, onStepChange, stepId]);

  const isBrowsingByLocation = currentStep === 'location-view';
  const preLocationViewStepRef = useRef<string>('opportunity');
  if (!isBrowsingByLocation) {
    preLocationViewStepRef.current = currentStep;
  }

  const formStepIds = useMemo(() => formSteps.map(s => s.id), [formSteps]);

  const handleToggleBrowseByLocation = useCallback(() => {
    if (currentStep === 'location-view') {
      if (formStepIds.length === 0) {
        return;
      }
      const previous = preLocationViewStepRef.current;
      const target = formStepIds.includes(previous) ? previous : formStepIds[0];
      onStepChange?.(target);
      return;
    }
    onStepChange?.('location-view');
  }, [currentStep, formStepIds, onStepChange]);

  const createDevicesUnitOverrides = useMemo(
    () => ({
      racks: formData.rackConfigurations ?? [],
      smallUps: formData.smallUpsConfigurations ?? [],
      largeUps: formData.largeUpsConfigurations ?? [],
      powerDistribution: formData.powerDistributionConfigurations ?? [],
      coolingSystems: formData.coolingSystemConfigurations ?? [],
      liquidCoolingCdus: formData.liquidCoolingCduConfigurations ?? [],
    }),
    [
      formData.rackConfigurations,
      formData.smallUpsConfigurations,
      formData.largeUpsConfigurations,
      formData.powerDistributionConfigurations,
      formData.coolingSystemConfigurations,
      formData.liquidCoolingCduConfigurations,
    ],
  );

  const handleDevicesUnitPress = useCallback(
    (deviceStepId: DeviceStepId, _unit: DeviceSourceUnit) => {
      if (recordingState !== 'idle') {
        return;
      }
      onStepChange?.(deviceStepId);
    },
    [onStepChange, recordingState],
  );

  const scopeChangeDepsRef = useRef({
    formDataEnabledStepIds: formData.enabledStepIds,
    formDataId: formData.id,
    formDataStage: (formData.stage || formData.sysRequestStage) as
      | string
      | undefined,
    isOnline,
    onStepChange,
    queryClient,
    stepId,
    updateRequestMutation,
  });
  scopeChangeDepsRef.current = {
    formDataEnabledStepIds: formData.enabledStepIds,
    formDataId: formData.id,
    formDataStage: (formData.stage || formData.sysRequestStage) as
      | string
      | undefined,
    isOnline,
    onStepChange,
    queryClient,
    stepId,
    updateRequestMutation,
  };

  const handleScopeChange = useCallback(
    async (nextStepIds: RequestFormStepId[]) => {
      const {
        formDataEnabledStepIds,
        formDataId,
        formDataStage,
        isOnline: online,
        onStepChange: onStepChangeFn,
        queryClient: scopeQueryClient,
        stepId: currentStepId,
        updateRequestMutation: mutation,
      } = scopeChangeDepsRef.current;
      const previousIds = formDataEnabledStepIds ?? null;
      const removedStepIds = diffRemovedOptionalStepIds(
        previousIds,
        nextStepIds,
      );
      const shouldPersistOnline = Boolean(formDataId && online);

      setFormData((prev: typeof formData) => ({
        ...prev,
        enabledStepIds: nextStepIds,
      }));

      if (shouldPersistOnline) {
        try {
          await mutation.mutateAsync({
            id: String(formDataId),
            data: { enabledStepIds: nextStepIds },
          });
        } catch (error: unknown) {
          console.error('Failed to persist scope change:', error);
          setFormData((prev: typeof formData) => ({
            ...prev,
            enabledStepIds: previousIds ?? prev.enabledStepIds,
          }));
          requestScopeRef.current?.revertScope(previousIds ?? null);
          showToast.error('Could not save scope changes. Please try again.');
          return;
        }
      } else if (formDataId) {
        try {
          await mutation.mutateAsync({
            id: String(formDataId),
            data: { enabledStepIds: nextStepIds },
          });
        } catch (error: unknown) {
          console.error('Failed to queue offline scope change:', error);
          setFormData((prev: typeof formData) => ({
            ...prev,
            enabledStepIds: previousIds ?? prev.enabledStepIds,
          }));
          requestScopeRef.current?.revertScope(previousIds ?? null);
          showToast.error('Could not save scope changes. Please try again.');
          return;
        }
      }

      if (removedStepIds.length > 0) {
        await applyScopeRemovalSideEffects({
          queryClient: scopeQueryClient,
          requestId: formDataId ? String(formDataId) : undefined,
          removedStepIds,
          nextStoredStepIds: nextStepIds,
          stage: formDataStage,
          handles: {
            discardOpenRackEditor: () =>
              rackSaveHandleRef.current?.discardOpenRackEditor(),
            resetRackSessionAfterScopeRemoval: () =>
              rackSaveHandleRef.current?.resetSessionAfterScopeRemoval(),
            discardOpenSmallUpsEditor: () =>
              smallUpsSaveHandleRef.current?.discardOpenSmallUpsEditor(),
            resetSmallUpsSessionAfterScopeRemoval: () =>
              smallUpsSaveHandleRef.current?.resetSessionAfterScopeRemoval(),
            discardOpenLargeUpsEditor: () =>
              largeUpsSaveHandleRef.current?.discardOpenLargeUpsEditor(),
            resetLargeUpsSessionAfterScopeRemoval: () =>
              largeUpsSaveHandleRef.current?.resetSessionAfterScopeRemoval(),
            discardOpenPowerDistributionEditor: () =>
              powerDistributionSaveHandleRef.current?.discardOpenPowerDistributionEditor(),
            resetPowerDistributionSessionAfterScopeRemoval: () =>
              powerDistributionSaveHandleRef.current?.resetSessionAfterScopeRemoval(),
            discardOpenCoolingSystemEditor: () =>
              coolingSystemSaveHandleRef.current?.discardOpenCoolingSystemEditor(),
            resetCoolingSystemSessionAfterScopeRemoval: () =>
              coolingSystemSaveHandleRef.current?.resetSessionAfterScopeRemoval(),
            discardOpenLiquidCoolingCduEditor: () =>
              liquidCoolingCduSaveHandleRef.current?.discardOpenLiquidCoolingCduEditor(),
            resetLiquidCoolingCduSessionAfterScopeRemoval: () =>
              liquidCoolingCduSaveHandleRef.current?.resetSessionAfterScopeRemoval(),
          },
        });

        setFormData((prev: typeof formData) =>
          stripFormFieldsForRemovedSteps(
            prev,
            removedStepIds,
            nextStepIds,
            (prev.stage || prev.sysRequestStage) as string | undefined,
          ),
        );
      }

      // Same exemption as the mount-time guard: a scope change must not drag the user off
      // the Deliverables section, which no scope selection can ever contain.
      if (
        currentStepId !== DELIVERABLES_STEP_ID &&
        !nextStepIds.includes(currentStepId as RequestFormStepId) &&
        onStepChangeFn
      ) {
        onStepChangeFn(nextStepIds[0] ?? 'opportunity');
      }
    },
    [],
  );

  const scopeStoredStepIds = formData.enabledStepIds ?? null;
  const scopeShowScopeControls = true;
  const scopeCanManageScope = !isManualSaving;

  const scopeRegistration = useMemo<RequestScopeRegistration>(
    () => ({
      storedStepIds: scopeStoredStepIds,
      showScopeControls: scopeShowScopeControls,
      canManageScope: scopeCanManageScope,
      onScopeChange: handleScopeChange,
    }),
    [
      scopeStoredStepIds,
      scopeShowScopeControls,
      scopeCanManageScope,
      handleScopeChange,
    ],
  );

  const scopeRegistrationBridgeActive = onScopeRegistrationChange != null;
  const scopeBridgeEffectDepsRef = useRef<Record<string, unknown> | null>(null);
  useRegisterRequestScope(
    scopeRegistrationBridgeActive ? null : scopeRegistration,
  );

  useLayoutEffect(() => {
    if (!scopeRegistrationBridgeActive) {
      return undefined;
    }

    const bridgePayload = {
      storedStepIds: scopeStoredStepIds,
      showScopeControls: scopeShowScopeControls,
      canManageScope: scopeCanManageScope,
      onScopeChange: handleScopeChange,
    };
    const deps = {
      scopeRegistrationBridgeActive,
      scopeStoredStepIds,
      scopeShowScopeControls,
      scopeCanManageScope,
      onScopeRegistrationChange,
    };
    logScopeEffectDeps(
      'CreateRequest scopeRegistration bridge',
      deps,
      scopeBridgeEffectDepsRef,
    );

    const fingerprint = registrationFingerprint(bridgePayload);
    const { shouldSkip } = checkScopeLoop(
      'CreateRequest.scopeBridge',
      fingerprint,
    );
    if (shouldSkip) {
      scopeLoopLog(
        'CreateRequest bridge SKIP onScopeRegistrationChange (loop guard)',
        {
          fingerprint,
        },
      );
      return undefined;
    }

    scopeLoopLog(
      'CreateRequest bridge call onScopeRegistrationChange(payload)',
      {
        fingerprint,
      },
    );
    onScopeRegistrationChange?.(bridgePayload);
    return undefined;
  }, [
    scopeRegistrationBridgeActive,
    scopeStoredStepIds,
    scopeShowScopeControls,
    scopeCanManageScope,
    onScopeRegistrationChange,
    handleScopeChange,
  ]);

  useEffect(() => {
    if (!scopeRegistrationBridgeActive || !onScopeRegistrationChange) {
      return undefined;
    }
    return () => {
      scopeLoopLog(
        'CreateRequest bridge unmount onScopeRegistrationChange(null)',
      );
      onScopeRegistrationChange(null);
    };
  }, [scopeRegistrationBridgeActive, onScopeRegistrationChange]);

  useEffect(() => {
    allowLeaveAfterCreateSaveRef.current = false;
    skipCreateDraftPersistRef.current = false;
  }, []);

  const flushDraftLocationTagsAfterSave = useCallback(
    async (
      serverRequestId: string | number,
      draftSectionWarnings: string[],
    ): Promise<Map<number, number>> => {
      if (!locationTagsDraft || locationTagsDraft.draftTags.length === 0) {
        return new Map();
      }

      const { warning, clientIdToServerId } =
        await flushDraftLocationTagsWithWarning(
          serverRequestId,
          locationTagsDraft.draftTags,
          locationTagsDraft.clearDraftTags,
        );

      if (warning) {
        draftSectionWarnings.push(warning);
        return new Map();
      }

      queryClient.invalidateQueries({
        queryKey: locationTagsKeys.list(String(serverRequestId)),
      });
      return clientIdToServerId;
    },
    [locationTagsDraft, queryClient],
  );

  const navigateToSavedRequest = useCallback(
    (
      requestId: string | number,
      delayMs = 1000,
      extra?: Record<string, string>,
    ) => {
      if (!isWeb) {
        requestSaveTransition.begin(requestId);
      }
      const go = () => {
        allowLeaveAfterCreateSaveRef.current = true;
        navigate(buildOpenedRequestPath(requestId, extra));
      };
      if (delayMs <= 0) {
        go();
        return;
      }
      setTimeout(go, delayMs);
    },
    [navigate, buildOpenedRequestPath, requestSaveTransition],
  );

  const persistCreateInlineImages = useCallback(
    async (
      requestId: string | number,
      formRecord: Record<string, unknown>,
      options?: { deferVersionCapture?: boolean },
    ) => {
      const stagedCreate = (await listOfflineInlineImages()).filter(
        item => item.requestId == null,
      );
      if (stagedCreate.length > 0 && !isWeb) {
        requestSaveTransition.setSavingMessage('Uploading images…');
      }
      return offlineRequestsService.persistStagedInlineImagesAfterCreate(
        requestId,
        formRecord,
        options,
      );
    },
    [requestSaveTransition],
  );

  const draftLocationTags = locationTagsDraft?.draftTags;

  const persistCreateDraft = useCallback(() => {
    if (skipCreateDraftPersistRef.current) {
      return;
    }
    if (!user?.email || !createDraftHydrateComplete) {
      return;
    }
    if (
      !hasCreateDraftContent(formData) &&
      !hasCreateDraftTags(draftLocationTags)
    ) {
      void clearCreateRequestDraftSnapshot(user.email);
      return;
    }
    void writeCreateRequestDraftSnapshot(user.email, {
      stepId,
      formData,
      locationTagsDraft: draftLocationTags,
    });
  }, [
    user?.email,
    formData,
    stepId,
    createDraftHydrateComplete,
    draftLocationTags,
  ]);

  const persistCreateDraftSync = useCallback(() => {
    if (skipCreateDraftPersistRef.current) {
      return;
    }
    if (!user?.email || !createDraftHydrateComplete) {
      return;
    }
    if (
      !hasCreateDraftContent(formData) &&
      !hasCreateDraftTags(draftLocationTags)
    ) {
      return;
    }
    writeCreateRequestDraftSnapshotSync(user.email, {
      stepId,
      formData,
      locationTagsDraft: draftLocationTags,
    });
  }, [
    user?.email,
    formData,
    stepId,
    createDraftHydrateComplete,
    draftLocationTags,
  ]);

  useDebouncedCallback(
    persistCreateDraft,
    [
      formData,
      stepId,
      user?.email,
      createDraftHydrateComplete,
      draftLocationTags,
    ],
    Boolean(user?.email && createDraftHydrateComplete),
  );

  useBeforeUnloadPersist(
    persistCreateDraftSync,
    Boolean(user?.email && createDraftHydrateComplete),
  );

  useEffect(() => {
    if (createDraftHydratedRef.current || !user?.email) {
      return;
    }
    createDraftHydratedRef.current = true;
    if (
      Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      isFreshCreateRequestQuery(window.location.search)
    ) {
      setCreateDraftHydrateComplete(true);
      return;
    }
    void (async () => {
      try {
        const draft = await readCreateRequestDraftSnapshot(user.email);
        const hasFormContent = Boolean(
          draft && hasCreateDraftContent(draft.formData),
        );
        const hasTags = Boolean(
          draft && hasCreateDraftTags(draft.locationTagsDraft),
        );
        if (draft && (hasFormContent || hasTags)) {
          if (hasFormContent) {
            // #1154 legacy *Instances migration removed (P3b Slice 2,
            // pre-#1333 window closed).
            const migratedDraftFormData = draft.formData;
            setFormData((prev: typeof formData) => {
              const merged = {
                ...prev,
                ...migratedDraftFormData,
              };
              const widestEnabledStepIds = resolveEnabledStepIds([
                ...resolveEnabledStepIds(prev.enabledStepIds),
                ...resolveEnabledStepIds(
                  migratedDraftFormData.enabledStepIds as
                    | string[]
                    | null
                    | undefined,
                ),
              ]);
              return {
                ...merged,
                enabledStepIds: widestEnabledStepIds,
              };
            });
            setCreateDraftSyncRevision(revision => revision + 1);
            onStepChange?.(draft.stepId);
          }
          if (hasTags && draft.locationTagsDraft) {
            locationTagsDraft?.hydrateDraftTags(draft.locationTagsDraft);
          }
          if (isBrowserReloadNavigation()) {
            showToast.info(REQUEST_DRAFT_RESTORED_MESSAGE);
          }
        }
      } finally {
        setCreateDraftHydrateComplete(true);
      }
    })();
  }, [user?.email, onStepChange, locationTagsDraft]);

  // File state management for all form sections
  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, SelectedFile[]>
  >({});

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] =
    useState<FileUploadProgress | null>(null);
  useInlineImageUploadFooterProgress(setUploadProgress);

  // Scroll to top when step changes
  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: false });
    }
  }, [stepId]);

  // Placeholder for future: trigger transcription upload when audio available
  useEffect(() => {
    // recordedAudioFile ready — future handling here
  }, [recordedAudioFile]);

  // Sidebar subtitles (#1094): live field progress + draft rack count without save
  useEffect(() => {
    setFormValues(formData as Record<string, unknown>);
  }, [formData, setFormValues]);

  useEffect(() => {
    return () => setFormValues(null);
  }, [setFormValues]);

  useEffect(() => {
    setShowFullDetails(false);
    setRequestStage(
      String(formData.stage || formData.sysRequestStage || 'Initial'),
    );
    setRequestId(formData.id ? String(formData.id) : 'new-request');
  }, [
    formData.id,
    formData.stage,
    formData.sysRequestStage,
    setRequestId,
    setRequestStage,
    setShowFullDetails,
  ]);

  useEffect(() => {
    return () => {
      setShowFullDetails(undefined);
      setRequestStage(undefined);
      setRequestId(undefined);
    };
  }, [setRequestId, setRequestStage, setShowFullDetails]);

  /**
   * FR13: deliverables entered before the request existed live in `formData`, like every
   * other field on an unsaved request, and are written once the server hands back an id.
   *
   * Called from the two **online** save paths only. The offline paths hand the same list to
   * `addToOfflineQueue` as a merged sub-object instead, and the sync flushes it once the
   * request has a real server id (spec C2).
   *
   * Never allowed to fail the save: the request itself is already created by this point,
   * and turning a storage hiccup into "failed to submit" would misdescribe what happened.
   */
  const persistFormDeliverables = useCallback(
    async (newRequestId: string | number, entered: unknown) => {
      if (!Array.isArray(entered) || entered.length === 0) return;
      try {
        // Empty base, and honestly so: the request was created a moment ago and holds no
        // deliverables, so every held row is an add and there is no deletion for the base
        // to authorise. The server now checks this rather than ignoring it.
        await saveDeliverables(
          String(newRequestId),
          [],
          entered as RequestDeliverable[],
        );
      } catch (error) {
        console.error(
          'Failed to save the deliverables onto the saved request:',
          error,
        );
        // Falling back to the queue rather than stopping at a console line, exactly as the
        // request create above this does. Swallowing here lost the user's whole checklist
        // and its typed comments with no toast, no retry and no record: they found out when
        // Submit later refused with "Add at least one deliverable".
        // (MR !489 review, finding 01 / the swallow cluster.)
        const queued = await offlineCache.queueDeliverables(
          String(newRequestId),
          // Same empty base, for the same reason: the PUT above is transactional, so its
          // failure leaves the freshly created request with nothing stored.
          [],
          entered as RequestDeliverable[],
        );
        if (!queued) {
          showToast.error(
            'Your request was saved, but its deliverables were not. Open the request and add them again.',
          );
        }
      }
    },
    [],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- full structured submit; footer uses handleSave
  const handleSubmit = useCallback(async () => {
    dismissActiveRichTextKeyboard();
    if (!(await waitForInlineImagePipelineIdle())) {
      showToast.error(
        'Wait for inline images to finish uploading before saving.',
      );
      return;
    }
    const opportunityRhf = opportunityFormFlushRef.current?.getValuesForSave();
    const submitFormData = rewritePreviewSrcsInRecord(
      (opportunityRhf != null
        ? { ...formData, ...opportunityRhf }
        : formData) as Record<string, unknown>,
    ) as typeof formData;

    if (opportunityRhf != null) {
      setFormData((prev: typeof formData) => ({ ...prev, ...opportunityRhf }));
    }

    // Comprehensive form validation
    const validationResult = validateRequiredFields(submitFormData);
    setValidationErrors(validationResult.errors);

    if (!validationResult.isValid) {
      setRhfValidationRevision(revision => revision + 1);
      const errorMessage = formatValidationErrors(validationResult.errors);
      showToast.error(errorMessage);
      return;
    }

    if (!isWeb) {
      requestSaveTransition.setSavingMessage('Saving request…');
    }

    // Process numeric fields to convert strings to numbers
    const processedData = prepareTextAreaFieldsForTransport(
      normalizeRequestNumericFields({ ...submitFormData }),
      { stripPendingAttachmentRefs: true },
    );

    skipCreateDraftPersistRef.current = true;
    await clearCreateRequestDraftSnapshot(user?.email);

    // Create request data WITHOUT team member fields or draft-only rows

    const {
      vertivSponsors,
      vertivContributors,
      partnerContributors,
      // Deliverables are their own records; the requests table has no column for them, so
      // they must not reach the create payload.
      deliverables: _deliverablesNotOnRequestsTable,
      ...requestDataWithoutTeam
    } = processedData;
    const { requestDataWithoutRepeatableDrafts, drafts: repeatableDrafts } =
      extractCreateRequestRepeatableDrafts(
        requestDataWithoutTeam as Record<string, unknown>,
      );
    let cacheDrafts = repeatableDrafts;

    const requestData: CreateRequestData = {
      // Basic information
      customerName: requestDataWithoutTeam.customerName,
      requestorName: requestDataWithoutTeam.requestorName,
      region: requestDataWithoutTeam.region,
      requestorRole: requestDataWithoutTeam.requestorRole,
      requestorEmail: requestDataWithoutTeam.requestorEmail,
      phone: requestDataWithoutTeam.requestorPhone,
      requestType: requestDataWithoutTeam.requestType || '',
      stage: requestDataWithoutTeam.stage || 'Initial',
      description: requestDataWithoutTeam.description || '',
      additionalDetails: requestDataWithoutTeam.additionalDetails || '',
      userEmail: user?.email || 'user@example.com',

      // Include form data fields EXCEPT team member fields and draft instance rows
      ...(requestDataWithoutRepeatableDrafts as typeof requestDataWithoutTeam),
    };

    const loadingToast = showToast.loading('Submitting request...');
    const dismissSaveLoadingToast = (): void => {
      showToast.dismiss(loadingToast);
    };

    try {
      setIsManualSaving(true);
      if (!isWeb) {
        requestSaveTransition.setSaving(true);
      }

      if (isOnline) {
        try {
          // When online: create and submit to server
          const createdRequest = await RequestsService.createAndSubmitRequest(
            requestData,
          );
          let flushedRequest;
          try {
            flushedRequest = await persistCreateInlineImages(
              createdRequest.id,
              submitFormData as unknown as Record<string, unknown>,
            );
          } catch (flushError) {
            if (flushError instanceof StagedInlineImageFlushError) {
              dismissSaveLoadingToast();
              skipCreateDraftPersistRef.current = false;
              clearManualSaveState();
              showToast.error(
                'Request saved but inline images could not be uploaded. Open the request and insert them again.',
              );
              return;
            }
            throw flushError;
          }
          const serverRequest = flushedRequest ?? createdRequest;

          await persistFormDeliverables(
            serverRequest.id,
            submitFormData.deliverables,
          );

          // Save team members using the dedicated Team Information API
          // Save if user selected Default Sponsors, even when deal is linked
          const isDealLinked =
            submitFormData.dealLinked === 'true' ||
            submitFormData.dealLinked === true;
          const hasDefaultSponsors =
            submitFormData.useDefaultSponsors === true ||
            submitFormData.useDefaultSponsors === 'true';
          if (!isDealLinked || hasDefaultSponsors) {
            await saveTeamMembersFromForm(serverRequest.id, submitFormData);
          }

          const draftSectionWarnings: string[] = [];
          const tagIdMap = await flushDraftLocationTagsAfterSave(
            serverRequest.id,
            draftSectionWarnings,
          );
          const racksToFlush = remapDraftLocationTagIds(
            repeatableDrafts.rackConfigurations,
            tagIdMap,
          );
          if (racksToFlush.length > 0) {
            try {
              const flushedRacks = await flushDraftRackConfigurations(
                serverRequest.id,
                racksToFlush,
              );
              cacheDrafts = {
                ...repeatableDrafts,
                rackConfigurations: flushedRacks,
              };
              queryClient.invalidateQueries({
                queryKey: rackConfigurationsKeys.list(String(serverRequest.id)),
              });
            } catch (rackError) {
              console.error(
                'Failed to save draft rack configurations:',
                rackError,
              );
              draftSectionWarnings.push('rack configurations');
            }
          }

          draftSectionWarnings.push(
            ...(await flushCreateRequestRepeatableDrafts(
              serverRequest.id,
              repeatableDrafts,
              { queryClient, locationTagIdMap: tagIdMap },
            )),
          );

          await persistCreateRequestRepeatableDraftsAfterSave(
            serverRequest.id,
            cacheDrafts,
            false,
            serverRequest as Record<string, unknown>,
          );

          primeCreateRequestRepeatableDraftsInCache(
            queryClient,
            serverRequest,
            cacheDrafts,
          );

          if (draftSectionWarnings.length > 0) {
            showToast.error(
              `Request submitted but ${draftSectionWarnings.join(
                ', ',
              )} could not be saved. You can add them from the relevant step.`,
            );
          }

          // Invalidate list to refresh with new item
          queryClient.invalidateQueries({
            queryKey: offlineRequestsKeys.lists(),
          });

          // Hide loading toast and show success
          dismissSaveLoadingToast();
          showToast.success('Request submitted successfully!');

          // Navigate after a short delay to let user see the success message
          navigateToSavedRequest(serverRequest.id);
        } catch (serverError) {
          console.error('Server submit failed:', serverError);
          dismissSaveLoadingToast();
          showToast.error('Failed to submit request. Please try again.');
        }
      } else {
        dismissSaveLoadingToast();
        showToast.error(
          'Cannot submit requests while offline. Please save as draft instead.',
        );
      }
    } catch (error) {
      console.error('Submit error:', error);
      showToast.error('An unexpected error occurred');
    } finally {
      if (!requestSaveTransition.hasActiveHandoff()) {
        clearManualSaveState();
      }
    }
  }, [
    formData,
    isOnline,
    queryClient,
    user,
    navigateToSavedRequest,
    persistFormDeliverables,
    persistCreateInlineImages,
    requestSaveTransition,
  ]);

  const handleSaveRequest = useCallback(async () => {
    dismissActiveRichTextKeyboard();
    if (!(await waitForInlineImagePipelineIdle())) {
      showToast.error(
        'Wait for inline images to finish uploading before saving.',
      );
      return;
    }
    const opportunityRhf = opportunityFormFlushRef.current?.getValuesForSave();
    const saveFormData = rewritePreviewSrcsInRecord(
      (opportunityRhf != null
        ? { ...formData, ...opportunityRhf }
        : formData) as Record<string, unknown>,
    ) as typeof formData;

    if (opportunityRhf != null) {
      setFormData((prev: typeof formData) => ({ ...prev, ...opportunityRhf }));
    }

    const validationResult = validateRequiredFields(saveFormData);
    setValidationErrors(validationResult.errors);

    if (!validationResult.isValid) {
      setRhfValidationRevision(revision => revision + 1);
      const errorMessage = formatValidationErrors(validationResult.errors);
      showToast.error(errorMessage);
      return;
    }

    if (!isWeb) {
      requestSaveTransition.setSavingMessage('Saving request…');
    }

    // Process numeric fields to convert strings to numbers
    const processedData = prepareTextAreaFieldsForTransport(
      normalizeRequestNumericFields({ ...saveFormData }),
      {
        allowPendingAttachmentRefs: !isOnline,
        stripPendingAttachmentRefs: isOnline,
      },
    );

    skipCreateDraftPersistRef.current = true;
    await clearCreateRequestDraftSnapshot(user?.email);

    // Create request data WITHOUT team member fields or draft-only rack rows

    const {
      vertivSponsors,
      vertivContributors,
      partnerContributors,
      // Deliverables are their own records; the requests table has no column for them, so
      // they must not reach the create payload.
      deliverables: _deliverablesNotOnRequestsTable,
      ...requestDataWithoutTeam
    } = processedData;
    const { requestDataWithoutRepeatableDrafts, drafts: repeatableDrafts } =
      extractCreateRequestRepeatableDrafts(
        requestDataWithoutTeam as Record<string, unknown>,
      );
    let cacheDrafts = repeatableDrafts;
    let aggregateCreateIdMap: Record<string, number> = {};
    let reloadedDimensionUnits: unknown;

    const requestData: CreateRequestData = {
      // Basic information
      customerName: requestDataWithoutTeam.customerName,
      requestorName: requestDataWithoutTeam.requestorName,
      region: requestDataWithoutTeam.region,
      requestorRole: requestDataWithoutTeam.requestorRole,
      requestorEmail: requestDataWithoutTeam.requestorEmail,
      phone: requestDataWithoutTeam.requestorPhone,
      requestType: requestDataWithoutTeam.requestType || '',
      stage: requestDataWithoutTeam.stage || 'Initial',
      description: requestDataWithoutTeam.description || '',
      additionalDetails: requestDataWithoutTeam.additionalDetails || '',
      enabledStepIds: resolveEnabledStepIds(saveFormData.enabledStepIds),

      // Include form data fields EXCEPT team member fields and draft instance rows
      ...(requestDataWithoutRepeatableDrafts as typeof requestDataWithoutTeam),
    };

    const loadingToast = showToast.loading('Saving request...');
    const dismissSaveLoadingToast = (): void => {
      showToast.dismiss(loadingToast);
    };

    try {
      setIsManualSaving(true);
      // #1501: stamp everything this create queues offline with one save
      // group (consumed by the future sync batcher; ended in finally below).
      offlineCache.beginOfflineSaveGroup();
      if (!isWeb) {
        requestSaveTransition.setSaving(true);
      }

      if (isOnline) {
        try {
          // #1501 aggregate create: when team/units follow, defer the create's
          // version capture — the single POST /:id/save below captures the
          // whole create as version 1 (one history entry per create, #25).
          const isDealLinkedForTeam =
            saveFormData.dealLinked === 'true' ||
            saveFormData.dealLinked === true;
          const hasDefaultSponsorsForTeam =
            saveFormData.useDefaultSponsors === true ||
            saveFormData.useDefaultSponsors === 'true';
          const teamWanted =
            hasTeamMemberChanges(saveFormData) &&
            (!isDealLinkedForTeam || hasDefaultSponsorsForTeam);
          const unitsWanted =
            repeatableDrafts.rackConfigurations.length > 0 ||
            repeatableDrafts.smallUpsConfigurations.length > 0 ||
            repeatableDrafts.largeUpsConfigurations.length > 0 ||
            repeatableDrafts.coolingSystemConfigurations.length > 0 ||
            repeatableDrafts.liquidCoolingCduConfigurations.length > 0 ||
            repeatableDrafts.powerDistributionConfigurations.length > 0;
          const deferCreateCapture =
            featureFlags.aggregateSave && (teamWanted || unitsWanted);

          // When online: save to server first for up-to-date data
          const createdRequest = await RequestsService.createRequest(
            requestData,
            { deferVersionCapture: deferCreateCapture },
          );
          let flushedRequest;
          try {
            flushedRequest = await persistCreateInlineImages(
              createdRequest.id,
              saveFormData as unknown as Record<string, unknown>,
              { deferVersionCapture: deferCreateCapture },
            );
          } catch (flushError) {
            if (flushError instanceof StagedInlineImageFlushError) {
              dismissSaveLoadingToast();
              skipCreateDraftPersistRef.current = false;
              clearManualSaveState();
              showToast.error(
                'Request saved but inline images could not be uploaded. Open the request and insert them again.',
              );
              return;
            }
            throw flushError;
          }
          const serverRequest = flushedRequest ?? createdRequest;

          await persistFormDeliverables(
            serverRequest.id,
            saveFormData.deliverables,
          );

          // Location tags first — both save paths below consume the id map
          // (draft rows reference draft tag ids).
          const draftSectionWarnings: string[] = [];
          const tagIdMap = await flushDraftLocationTagsAfterSave(
            serverRequest.id,
            draftSectionWarnings,
          );

          // ── #1501 aggregate create: ONE POST /:id/save carries the team and
          // every drafted unit; with the deferred create capture above, its
          // single version capture becomes v1 for the whole create. Any
          // failure (incl. 404 AGGREGATE_SAVE_DISABLED) falls back to the
          // legacy per-section sequence below.
          let aggregateCreateDone = false;
          if (deferCreateCapture) {
            try {
              const teamRows = teamWanted
                ? await buildTeamMembersFromForm(
                    String(serverRequest.id),
                    saveFormData,
                  )
                : null;
              const familySegments = buildAggregateCreateFamilySegments(
                repeatableDrafts,
                tagIdMap,
                serverRequest.id,
              );
              const result = await RequestsService.saveAggregate(
                String(serverRequest.id),
                {
                  ...aggregateCreateDimensionUnitsSegment(
                    saveFormData.dimensionUnits,
                  ),
                  ...(teamRows !== null ? { team: teamRows as never } : {}),
                  ...familySegments,
                },
              );
              aggregateCreateIdMap = result.idMap ?? {};
              cacheDrafts = applyAggregateCreateIdMap(
                repeatableDrafts,
                aggregateCreateIdMap,
              );
              await invalidateRepeatableFamilyQueries(
                queryClient,
                serverRequest.id,
              );
              if (result.status === 'partial') {
                draftSectionWarnings.push(
                  String(result.failed?.section ?? 'some sections'),
                );
              }
              aggregateCreateDone = true;
            } catch (aggregateError) {
              // Fall back to the legacy sequence — its calls capture versions
              // per call (degraded history granularity, but nothing is lost).
              console.error(
                'Aggregate create failed — falling back to per-section saves:',
                aggregateError,
              );
            }
          }

          if (!aggregateCreateDone) {
            // Save team members using the dedicated Team Information API
            // Save if user selected Default Sponsors, even when deal is linked
            if (teamWanted) {
              await saveTeamMembersFromForm(serverRequest.id, saveFormData);
            }

            const racksToFlush = remapDraftLocationTagIds(
              repeatableDrafts.rackConfigurations,
              tagIdMap,
            );
            if (racksToFlush.length > 0) {
              try {
                const flushedRacks = await flushDraftRackConfigurations(
                  serverRequest.id,
                  racksToFlush,
                );
                cacheDrafts = {
                  ...repeatableDrafts,
                  rackConfigurations: flushedRacks,
                };
                queryClient.invalidateQueries({
                  queryKey: rackConfigurationsKeys.list(
                    String(serverRequest.id),
                  ),
                });
              } catch (rackError) {
                console.error(
                  'Failed to save draft rack configurations:',
                  rackError,
                );
                draftSectionWarnings.push('rack configurations');
              }
            }

            draftSectionWarnings.push(
              ...(await flushCreateRequestRepeatableDrafts(
                serverRequest.id,
                repeatableDrafts,
                { queryClient, locationTagIdMap: tagIdMap },
              )),
            );
            const hadChipRows =
              repeatableDrafts.rackConfigurations.length > 0 ||
              repeatableDrafts.coolingSystemConfigurations.length > 0 ||
              repeatableDrafts.powerDistributionConfigurations.length > 0;
            if (hadChipRows) {
              try {
                const fresh = await RequestsService.getRequestById(
                  String(serverRequest.id),
                );
                reloadedDimensionUnits = fresh.dimensionUnits;
              } catch (reloadError) {
                console.error(
                  'Failed to reload unit chips after create:',
                  reloadError,
                );
              }
            }
          }

          await persistCreateRequestRepeatableDraftsAfterSave(
            serverRequest.id,
            cacheDrafts,
            false,
            serverRequest as Record<string, unknown>,
          );

          if (draftSectionWarnings.length > 0) {
            showToast.error(
              `Request saved but ${draftSectionWarnings.join(
                ', ',
              )} could not be saved. You can add them from the relevant step.`,
            );
          }

          // Upload files if any exist
          if (Object.keys(selectedFiles).length > 0) {
            dismissSaveLoadingToast();
            if (!isWeb) {
              requestSaveTransition.setSavingMessage('Uploading files…');
            }
            const uploadingToast = isWeb
              ? undefined
              : showToast.loading('Uploading files...');

            try {
              const requestId =
                typeof serverRequest.id === 'string'
                  ? parseInt(serverRequest.id, 10)
                  : serverRequest.id;
              const uploadResult =
                await fileUploadService.uploadFilesForRequest(
                  requestId,
                  selectedFiles,
                  progress => {
                    setUploadProgress(progress);
                  },
                );

              if (uploadingToast != null) {
                showToast.dismiss(uploadingToast);
              }
              if (isWeb) {
                setUploadProgress(null);
              } else {
                // Don't clear progress bar yet - keep showing save progress during cache update
                setUploadProgress({
                  section: 'request-data',
                  fileName: 'Finalizing save...',
                  progress: 100,
                  status: 'uploading',
                });
              }

              if (uploadResult.success) {
                if (uploadResult.uploadedCount > 0) {
                  markAttachmentsUploadedForRequest(String(serverRequest.id));
                  debugAttachmentScope('CreateRequest.upload', {
                    requestId: serverRequest.id,
                    uploadedCount: uploadResult.uploadedCount,
                    failedCount: uploadResult.failedCount,
                    sections: Object.keys(selectedFiles),
                    uploadedFiles: uploadResult.uploadedFiles,
                  });
                }
                // Don't show toast yet - wait until cache is updated
              } else if (uploadResult.uploadedCount > 0) {
                markAttachmentsUploadedForRequest(String(serverRequest.id));
                console.warn(
                  `⚠️ Partial upload: ${uploadResult.uploadedCount} succeeded, ${uploadResult.failedCount} failed`,
                );
                // Store for later display
              } else {
                console.error(
                  '❌ All file uploads failed:',
                  uploadResult.errors,
                );
                setUploadProgress(null);
                showToast.error(
                  `Request saved but all ${uploadResult.failedCount} file(s) failed to upload. Error: ${uploadResult.errors[0]}`,
                );
              }
            } catch (uploadError) {
              console.error('❌ File upload exception:', uploadError);
              if (uploadingToast != null) {
                showToast.dismiss(uploadingToast);
              }
              setUploadProgress(null); // Clear progress bar on error
              const errorMsg =
                uploadError instanceof Error
                  ? uploadError.message
                  : 'Unknown error';
              showToast.error(
                `Request saved but file upload failed: ${errorMsg}. You can add files later from the request detail page.`,
              );
            }
          } else {
            dismissSaveLoadingToast();
          }

          // Update the cache with the server response (merge scope + repeatable drafts)
          const remappedDimensionUnits = remapAggregateCreateDimensionUnits(
            (aggregateCreateIdMap && Object.keys(aggregateCreateIdMap).length > 0
              ? saveFormData.dimensionUnits
              : reloadedDimensionUnits ?? saveFormData.dimensionUnits) ??
              (serverRequest as { dimensionUnits?: unknown }).dimensionUnits,
            aggregateCreateIdMap,
          );
          const cachedRequest = {
            // Create responses can be intentionally sparse. Seed detail view
            // with the values that were just validated and saved so native
            // never paints a temporary `—` while its follow-up read settles.
            ...saveFormData,
            ...serverRequest,
            ...(Object.keys(remappedDimensionUnits).length > 0
              ? { dimensionUnits: remappedDimensionUnits }
              : {}),
            enabledStepIds:
              serverRequest.enabledStepIds ??
              saveFormData.enabledStepIds ??
              null,
            powerAdditionalInformation:
              serverRequest.powerAdditionalInformation ??
              saveFormData.powerAdditionalInformation ??
              '',
          };
          queryClient.setQueryData(
            offlineRequestsKeys.detail(serverRequest.id),
            cachedRequest,
          );

          // Add the new request to the beginning of the cached list
          queryClient.setQueryData(
            offlineRequestsKeys.lists(),
            (oldData: unknown) => {
              return !oldData
                ? [cachedRequest]
                : [cachedRequest, ...(oldData as unknown[])];
            },
          );

          primeCreateRequestRepeatableDraftsInCache(
            queryClient,
            cachedRequest,
            cacheDrafts,
          );

          // Invalidate both the offline and paginated queries to mark them as stale
          queryClient.invalidateQueries({
            queryKey: offlineRequestsKeys.lists(),
          });
          queryClient.invalidateQueries({ queryKey: requestsKeys.all }); // Invalidate all paginated queries

          // Show success toast based on file upload results
          const hasFiles = Object.keys(selectedFiles).length > 0;
          if (hasFiles) {
            const fileCount = Object.values(selectedFiles).reduce(
              (sum, files) => sum + files.length,
              0,
            );
            showToast.success(
              `Request and ${fileCount} file(s) saved successfully!`,
            );
          } else {
            showToast.success('Request saved successfully!');
          }

          // Navigate after save completes — web uses footer progress only.
          navigateToSavedRequest(serverRequest.id);
          setUploadProgress(null);
        } catch (serverError) {
          // Bug #1 (#1339): distinguish a genuine offline/network failure from a
          // server-side REJECTION. `ApiError.status === 0` means no HTTP response
          // reached us (network down, timeout, server unreachable) — a real offline
          // case worth an offline draft. A non-zero status means the SERVER responded
          // with an error (400/403/500…): the request was NOT saved anywhere. Masking
          // that as "saved offline" strands data in a broken sync state and hides the
          // real cause (e.g. missing migration, permission strip). Surface the actual
          // error and keep the user on the form to retry.
          if (serverError instanceof ApiError && serverError.status !== 0) {
            console.error(
              `Server rejected request save (HTTP ${serverError.status}):`,
              serverError.message,
            );
            // Keep persisting the local draft (user stays on the form to retry) —
            // mirrors the outer catch's reset so their entries aren't lost.
            skipCreateDraftPersistRef.current = false;
            dismissSaveLoadingToast();
            setUploadProgress(null);
            clearManualSaveState();
            showToast.error(`Couldn't save request: ${serverError.message}`);
            return;
          }

          console.warn(
            'Server save failed (no response) — falling back to offline save:',
            serverError,
          );
          // Genuine offline / network failure — fall back to offline save

          // Extract team member data from formData
          const teamMemberData = hasTeamMemberChanges(saveFormData)
            ? {
                vertivSponsors: saveFormData.vertivSponsors || [],
                vertivContributors: saveFormData.vertivContributors || [],
                // Objects, not emails: collapsing them loses `accessLevel` and the replay
                // silently demotes a read_write partner to read-only.
                partnerContributors: saveFormData.partnerContributors || [],
                // The online path hands `saveTeamMembersFromForm` the whole form, so it
                // resolves `default_sponsor_nam/emea/apac`. Queue the two inputs it needs
                // or the replay always falls back to plain `vertiv_sponsor`.
                useDefaultSponsors:
                  saveFormData.useDefaultSponsors === true ||
                  saveFormData.useDefaultSponsors === 'true',
                region: saveFormData.region,
              }
            : undefined;

          // Deliverables ride the create queue as a merged sub-object and are flushed
          // once the request has a real id. `undefined` when the form never touched them,
          // so the sync can tell "no opinion" from a deliberate empty list.
          const offlineDeliverables = Array.isArray(saveFormData.deliverables)
            ? heldDeliverables(saveFormData)
            : undefined;

          const offlineId = await offlineCache.addToOfflineQueue(
            requestData,
            teamMemberData,
            offlineDeliverables,
          );

          if (repeatableDrafts.rackConfigurations.length > 0) {
            const flushedRacks = await flushDraftRackConfigurations(
              offlineId,
              repeatableDrafts.rackConfigurations,
              true,
            );
            cacheDrafts = {
              ...repeatableDrafts,
              rackConfigurations: flushedRacks,
            };
          }
          await flushCreateRequestRepeatableDrafts(
            offlineId,
            repeatableDrafts,
            {
              isOfflineRequest: true,
            },
          );
          await persistCreateRequestRepeatableDraftsAfterSave(
            offlineId,
            cacheDrafts,
            true,
          );

          const primedOffline = await offlineRequestsService.getRequestById(
            offlineId,
          );
          if (primedOffline) {
            primeCreateRequestRepeatableDraftsInCache(
              queryClient,
              primedOffline,
              cacheDrafts,
            );
          }

          // Trigger a refetch to include the offline request
          await queryClient.refetchQueries({
            queryKey: offlineRequestsKeys.lists(),
          });

          // Hide loading toast, re-enable UI, and show offline success
          dismissSaveLoadingToast();
          showToast.success(
            'Server unavailable - saved offline! You can view and edit it. Will sync when online.',
          );

          navigateToSavedRequest(offlineId);
        }
      } else {
        // When offline: save to offline cache and show optimistically
        // Offline: save to offline cache

        // Extract team member data from formData
        const teamMemberData = hasTeamMemberChanges(saveFormData)
          ? {
              vertivSponsors: saveFormData.vertivSponsors || [],
              vertivContributors: saveFormData.vertivContributors || [],
              // See the server-save fallback above: objects preserve `accessLevel`, and
              // the two sponsor-role inputs keep the replay's role resolution honest.
              partnerContributors: saveFormData.partnerContributors || [],
              useDefaultSponsors:
                saveFormData.useDefaultSponsors === true ||
                saveFormData.useDefaultSponsors === 'true',
              region: saveFormData.region,
            }
          : undefined;

        // See the server-save fallback above: whole list, `undefined` when untouched.
        const offlineDeliverables = Array.isArray(saveFormData.deliverables)
          ? heldDeliverables(saveFormData)
          : undefined;

        const offlineId = await offlineCache.addToOfflineQueue(
          requestData,
          teamMemberData,
          offlineDeliverables,
        );

        if (repeatableDrafts.rackConfigurations.length > 0) {
          const flushedRacks = await flushDraftRackConfigurations(
            offlineId,
            repeatableDrafts.rackConfigurations,
            true,
          );
          cacheDrafts = {
            ...repeatableDrafts,
            rackConfigurations: flushedRacks,
          };
        }
        await flushCreateRequestRepeatableDrafts(offlineId, repeatableDrafts, {
          isOfflineRequest: true,
        });
        await persistCreateRequestRepeatableDraftsAfterSave(
          offlineId,
          cacheDrafts,
          true,
        );

        const primedOffline = await offlineRequestsService.getRequestById(
          offlineId,
        );
        if (primedOffline) {
          primeCreateRequestRepeatableDraftsInCache(
            queryClient,
            primedOffline,
            cacheDrafts,
          );
        }

        // Trigger a refetch to include the new offline item
        await queryClient.refetchQueries({
          queryKey: offlineRequestsKeys.lists(),
        });

        // Hide loading toast, re-enable UI, and show offline success
        dismissSaveLoadingToast();
        showToast.success(
          'Request saved offline! You can view and edit it. Will sync when online.',
        );

        navigateToSavedRequest(offlineId);
      }

      // Request saved successfully
    } catch (error) {
      skipCreateDraftPersistRef.current = false;
      console.error('Failed to save request:', error);
      showToast.error('Failed to save request. Please try again.');
    } finally {
      offlineCache.endOfflineSaveGroup();
      if (!requestSaveTransition.hasActiveHandoff()) {
        clearManualSaveState();
      }
    }
  }, [
    formData,
    inlineImageUploadsInFlight,
    isOnline,
    queryClient,
    selectedFiles,
    user?.email,
    navigateToSavedRequest,
    persistFormDeliverables,
    persistCreateInlineImages,
    requestSaveTransition,
  ]);

  const handleFooterSave = useCallback(async () => {
    if (repeatableSaveTarget) {
      setIsManualSaving(true);
      try {
        const instanceSave = instanceSaveHandleRef.current;
        const rackSave = rackSaveHandleRef.current;
        const smallUpsSave = smallUpsSaveHandleRef.current;
        const largeUpsSave = largeUpsSaveHandleRef.current;
        const powerDistributionSave = powerDistributionSaveHandleRef.current;
        const coolingSystemSave = coolingSystemSaveHandleRef.current;
        const liquidCoolingCduSave = liquidCoolingCduSaveHandleRef.current;
        const committed = await commitRepeatableSaveTarget(
          repeatableSaveTarget,
          {
            'facility-power': instanceSave
              ? {
                  isOpen: instanceSave.isInstanceEditorOpen,
                  commit: instanceSave.commitOpenInstanceEditor,
                }
              : undefined,
            rack: rackSave
              ? {
                  isOpen: rackSave.isRackEditorOpen,
                  commit: rackSave.commitOpenRackEditor,
                }
              : undefined,
            'small-ups': smallUpsSave
              ? {
                  isOpen: smallUpsSave.isSmallUpsEditorOpen,
                  commit: smallUpsSave.commitOpenSmallUpsEditor,
                }
              : undefined,
            'large-ups': largeUpsSave
              ? {
                  isOpen: largeUpsSave.isLargeUpsEditorOpen,
                  commit: largeUpsSave.commitOpenLargeUpsEditor,
                }
              : undefined,
            'power-distribution': powerDistributionSave
              ? {
                  isOpen: powerDistributionSave.isPowerDistributionEditorOpen,
                  commit:
                    powerDistributionSave.commitOpenPowerDistributionEditor,
                }
              : undefined,
            cooling: coolingSystemSave
              ? {
                  isOpen: coolingSystemSave.isCoolingSystemEditorOpen,
                  commit: coolingSystemSave.commitOpenCoolingSystemEditor,
                }
              : undefined,
            'liquid-cooling': liquidCoolingCduSave
              ? {
                  isOpen: liquidCoolingCduSave.isLiquidCoolingCduEditorOpen,
                  commit: liquidCoolingCduSave.commitOpenLiquidCoolingCduEditor,
                }
              : undefined,
          },
        );
        const feedback = {
          'facility-power': [
            'Facility Power saved.',
            'Could not save Facility Power. Please try again.',
          ],
          rack: ['Rack saved.', 'Could not save rack. Please try again.'],
          'small-ups': [
            'Small UPS saved.',
            'Could not save small UPS. Please try again.',
          ],
          'large-ups': [
            'Large UPS saved.',
            'Could not save large UPS. Please try again.',
          ],
          'power-distribution': [
            'Power distribution saved.',
            'Could not save power distribution. Please try again.',
          ],
          cooling: [
            'Cooling system saved.',
            'Could not save cooling system. Please try again.',
          ],
          'liquid-cooling': [
            'CDU saved.',
            'Could not save CDU. Please try again.',
          ],
        } as const;
        if (committed) {
          showToast.success(feedback[repeatableSaveTarget][0]);
        } else {
          showToast.error(feedback[repeatableSaveTarget][1]);
        }
      } finally {
        clearManualSaveState();
      }
      return;
    }
    await handleSaveRequest();
  }, [handleSaveRequest, repeatableSaveTarget]);

  const tryLeaveCreateRequest = useCallback(async (): Promise<boolean> => {
    if (allowLeaveAfterCreateSaveRef.current) {
      return true;
    }

    const rackSave = rackSaveHandleRef.current;
    const instanceSave = instanceSaveHandleRef.current;
    const largeUpsSave = largeUpsSaveHandleRef.current;
    const powerDistributionSave = powerDistributionSaveHandleRef.current;
    const smallUpsSave = smallUpsSaveHandleRef.current;
    const coolingSystemSave = coolingSystemSaveHandleRef.current;
    const liquidCoolingCduSave = liquidCoolingCduSaveHandleRef.current;
    const hasInstanceEditor = instanceSave?.hasUnsavedInstanceEditor() ?? false;
    const hasLargeUpsEditor = largeUpsSave?.hasUnsavedLargeUpsEditor() ?? false;
    const hasPowerDistributionEditor =
      powerDistributionSave?.hasUnsavedPowerDistributionEditor() ?? false;
    const hasSmallUpsEditor = smallUpsSave?.hasUnsavedSmallUpsEditor() ?? false;
    const hasCoolingSystemEditor =
      coolingSystemSave?.hasUnsavedCoolingSystemEditor() ?? false;
    const hasLiquidCoolingCduEditor =
      liquidCoolingCduSave?.hasUnsavedLiquidCoolingCduEditor() ?? false;
    const hasRackEditor = rackSave?.hasUnsavedRackEditor() ?? false;
    const hasFiles = Object.values(selectedFiles).some(
      files => files.length > 0,
    );
    const hasForm =
      createDraftHydrateComplete && hasCreateDraftContent(formData);

    if (
      !hasInstanceEditor &&
      !hasSmallUpsEditor &&
      !hasCoolingSystemEditor &&
      !hasLiquidCoolingCduEditor &&
      !hasRackEditor &&
      !hasLargeUpsEditor &&
      !hasPowerDistributionEditor &&
      !hasForm &&
      !hasFiles
    ) {
      return true;
    }

    const confirmed = await confirm({
      title: RACK_DISCARD_UNSAVED_TITLE,
      message: RACK_DISCARD_UNSAVED_MESSAGE,
      confirmText: RACK_DISCARD_UNSAVED_DISCARD_LABEL,
      cancelText: RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return false;
    }

    instanceSave?.discardOpenInstanceEditor();
    smallUpsSave?.discardOpenSmallUpsEditor();
    coolingSystemSave?.discardOpenCoolingSystemEditor();
    liquidCoolingCduSave?.discardOpenLiquidCoolingCduEditor();
    rackSave?.discardOpenRackEditor();
    largeUpsSave?.discardOpenLargeUpsEditor();
    powerDistributionSave?.discardOpenPowerDistributionEditor();
    if (user?.email) {
      await clearCreateRequestDraftSnapshot(user.email);
    }
    return true;
  }, [
    confirm,
    createDraftHydrateComplete,
    formData,
    selectedFiles,
    user?.email,
  ]);

  useRegisterRequestLeaveGuard(tryLeaveCreateRequest);

  const tryChangeRequestStep = useCallback(
    async (nextStepId: string): Promise<boolean> => {
      if (nextStepId === stepId) {
        return true;
      }
      const instanceSave = instanceSaveHandleRef.current;
      if (instanceSave?.isInstanceEditorOpen()) {
        const mayLeave = await instanceSave.tryDismissInstanceEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        instanceSave?.discardOpenInstanceEditor();
      }
      const largeUpsSave = largeUpsSaveHandleRef.current;
      if (largeUpsSave?.isLargeUpsEditorOpen()) {
        const mayLeave = await largeUpsSave.tryDismissLargeUpsEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        largeUpsSave?.discardOpenLargeUpsEditor();
      }
      const powerDistributionSave = powerDistributionSaveHandleRef.current;
      if (powerDistributionSave?.isPowerDistributionEditorOpen()) {
        const mayLeave =
          await powerDistributionSave.tryDismissPowerDistributionEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        powerDistributionSave?.discardOpenPowerDistributionEditor();
      }
      const smallUpsSave = smallUpsSaveHandleRef.current;
      if (smallUpsSave?.isSmallUpsEditorOpen()) {
        const mayLeave = await smallUpsSave.tryDismissSmallUpsEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        smallUpsSave?.discardOpenSmallUpsEditor();
      }
      const coolingSystemSave = coolingSystemSaveHandleRef.current;
      if (coolingSystemSave?.isCoolingSystemEditorOpen()) {
        const mayLeave =
          await coolingSystemSave.tryDismissCoolingSystemEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        coolingSystemSave?.discardOpenCoolingSystemEditor();
      }
      const liquidCoolingCduSave = liquidCoolingCduSaveHandleRef.current;
      if (liquidCoolingCduSave?.isLiquidCoolingCduEditorOpen()) {
        const mayLeave =
          await liquidCoolingCduSave.tryDismissLiquidCoolingCduEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        liquidCoolingCduSave?.discardOpenLiquidCoolingCduEditor();
      }
      rackSaveHandleRef.current?.discardOpenRackEditor();
      return true;
    },
    [stepId],
  );

  useRegisterRequestStepChangeGuard(tryChangeRequestStep);

  const handleFilesSelected = (section: string, files: SelectedFile[]) => {
    setSelectedFiles(prev => {
      const updated = {
        ...prev,
        [section]: files,
      };
      if (section === SECTION_IDS.SITE_DIMENSIONS_INFO) {
        updated[SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL] = [];
      }
      return updated;
    });
  };

  /**
   * Handle recording button press - shows warning modal first
   */
  const handleRecordingButtonPress = () => {
    setShowRecordingWarning(true);
  };

  /**
   * #1149 Issue 9 — compact footer tools. Create Request has no Archive, so
   * this menu is intake-only and renders without section headings.
   */
  const isCompactFooter = useCompactRequestFooter();
  const footerToolItems: RequestToolsMenuItem[] = [];
  {
    const intakeBusy = isManualSaving || !isOnline;
    footerToolItems.push({
      key: 'record',
      label: 'Record audio',
      hint: isOnline
        ? 'Use audio to update or create the request'
        : 'Needs a connection',
      icon: <Mic size={20} color={colors.textPrimary} />,
      onPress: () => {
        if (!intakeBusy) {
          handleRecordingButtonPress();
        }
      },
      testID: 'create-record-audio-button',
    });
    if (draftButtonPermissions.canShowTextIntake) {
      footerToolItems.push({
        key: 'paste-text',
        label: 'Paste text',
        hint: isOnline
          ? 'Paste text from emails or notes to update or create the request'
          : 'Needs a connection',
        icon: <TextIcon size={20} color={colors.textPrimary} />,
        onPress: () => {
          if (!intakeBusy) {
            setTextIntakeModalVisible(true);
          }
        },
        testID: 'create-paste-text-button',
      });
    }
  }

  /**
   * Handle recording warning confirmation - starts recording
   */
  const handleRecordingWarningConfirm = () => {
    setShowRecordingWarning(false);
    onStartRecording();
  };

  /**
   * Handle recording warning cancellation - closes modal
   */
  const handleRecordingWarningCancel = () => {
    setShowRecordingWarning(false);
  };

  /**
   * Handle recording acceptance - uploads for transcription
   */
  const handleAcceptRecordingWithUpload = useCallback(async () => {
    await onAcceptRecording(async (audioFile: AudioFile) => {
      try {
        // Validate only requestor fields (user info must be present)
        // Customer name and region can be [BLANK] - AI will fill them from transcription
        if (!formData.requestorName || formData.requestorName.trim() === '') {
          throw new Error('Requestor Name is required for transcription');
        }
        if (!formData.requestorEmail || formData.requestorEmail.trim() === '') {
          throw new Error('Requestor Email is required for transcription');
        }

        // Set customer name and region to [BLANK] if empty (AI will fill from transcription)
        // This matches the behavior of "Create Request from Audio" flow
        const customerName = formData.customerName?.trim() || '[BLANK]';
        const region = formData.region?.trim() || '[BLANK]';
        const requestorName = formData.requestorName.trim();
        const requestorEmail = formData.requestorEmail.trim();

        // Rename audio file with user/request context
        const newFilename = generateAudioFilename({
          originalFilename: audioFile.name,
          userEmail: user?.email,
          requestId: 'new',
        });
        const renamedFile = renameAudioFile(audioFile, newFilename);

        // Prepare file for upload
        const fileData = {
          uri: renamedFile.uri,
          name: renamedFile.name,
          type: renamedFile.mimeType,
          size: renamedFile.size,
          webFile: renamedFile.webFile, // Web: File object, Native: undefined
        };

        // Call transcription API
        const response = await createWithImportMutation.mutateAsync({
          customerName,
          region,
          requestorName,
          requestorEmail,
          file: fileData,
        });

        // Same leave-guard bypass as footer Save (#1479). Audio accept is an
        // intended leave to the new request's processing UI.
        // No success toast — Request Detail already shows Processing / Complete
        // modals (same as in-request re-record). A "uploaded" toast right before
        // that modal reads as done when the job has only started.
        skipCreateDraftPersistRef.current = true;
        await clearCreateRequestDraftSnapshot(user?.email);
        allowLeaveAfterCreateSaveRef.current = true;

        navigateToSavedRequest(response.requestId, 500, {
          showProcessing: 'true',
          fileName: renamedFile.name,
        });
      } catch (error) {
        console.error('Transcription upload error:', error);

        // Show user-friendly error message
        if (
          error instanceof Error &&
          error.message.includes('required for transcription')
        ) {
          showToast.error(error.message);
        } else {
          showToast.error(
            'Failed to upload audio for transcription. Please try again.',
          );
        }

        throw error; // Re-throw so the recording hook can handle state cleanup
      }
    });
  }, [
    formData,
    onAcceptRecording,
    createWithImportMutation,
    navigateToSavedRequest,
    user,
  ]);

  // Submit pasted text -> create the request from it (mirrors the audio accept
  // flow above: requestor fields must be present; customer/region fall back to
  // [BLANK] for the AI to fill from the text).
  const handleTextIntakeSubmit = useCallback(
    async (text: string, options: { emailThread?: boolean } = {}) => {
      if (!formData.requestorName || formData.requestorName.trim() === '') {
        showToast.error('Requestor Name is required for text intake');
        return;
      }
      if (!formData.requestorEmail || formData.requestorEmail.trim() === '') {
        showToast.error('Requestor Email is required for text intake');
        return;
      }
      setTextIntakeSubmitting(true);
      try {
        const response = await transcriptionService.createRequestFromText({
          customerName: formData.customerName?.trim() || '[BLANK]',
          region: formData.region?.trim() || '[BLANK]',
          requestorName: formData.requestorName.trim(),
          requestorEmail: formData.requestorEmail.trim(),
          text,
          emailThread: options.emailThread,
        });
        setTextIntakeModalVisible(false);
        // Same leave-guard bypass as the audio accept flow: this is an
        // intended leave to the new request's processing UI.
        skipCreateDraftPersistRef.current = true;
        await clearCreateRequestDraftSnapshot(user?.email);
        allowLeaveAfterCreateSaveRef.current = true;
        navigateToSavedRequest(response.requestId, 500, {
          showProcessing: 'true',
          fileName: 'Pasted text',
        });
      } catch (error) {
        console.error('Failed to create request from text:', error);
        showToast.error(
          error instanceof Error
            ? error.message
            : 'Failed to create request from text.',
        );
      } finally {
        setTextIntakeSubmitting(false);
      }
    },
    [formData, navigateToSavedRequest, user],
  );

  const updateFormData = useCallback(
    (field: string, value: string | number | boolean | string[] | any[]) => {
      setFormData((prev: any) => ({
        ...prev,
        [field]: value,
      }));

      // Clear validation error for this field when user starts typing
      setValidationErrors(prev => prev.filter(error => error.field !== field));
    },
    [],
  ); // Empty dependencies - this function doesn't depend on any external values

  /**
   * Deliverables on an unsaved request are ordinary form state (FR13). Held in
   * `formData.deliverables` rather than in storage of their own, so abandoning the form
   * discards them exactly as it discards every other field — the write-through build kept
   * them under a `new-request` storage key, where the *next* new request found them.
   *
   * `setFormData` directly rather than `updateFormData`: these never carry a validation
   * error to clear, and the functional form is what keeps two quick presses from racing.
   */
  const formDeliverables: RequestDeliverable[] = useMemo(
    () => (Array.isArray(formData.deliverables) ? formData.deliverables : []),
    [formData.deliverables],
  );

  const handleDeliverablesAdd = useCallback(
    (entries: RequestDeliverableEntry[]) => {
      setFormData((prev: typeof formData) => ({
        ...prev,
        deliverables: applyDeliverableEntries(
          heldDeliverables(prev),
          prev.id ? String(prev.id) : NEW_REQUEST_DELIVERABLES_ID,
          entries,
        ),
      }));
    },
    [],
  );

  const handleDeliverablesEdit = useCallback(
    (itemType: RequestDeliverableType, comment: string) => {
      setFormData((prev: typeof formData) => ({
        ...prev,
        deliverables: applyDeliverableComment(
          heldDeliverables(prev),
          itemType,
          comment,
        ),
      }));
    },
    [],
  );

  const handleDeliverablesRemove = useCallback(
    (itemType: RequestDeliverableType) => {
      setFormData((prev: typeof formData) => ({
        ...prev,
        deliverables: withoutDeliverableType(heldDeliverables(prev), itemType),
      }));
    },
    [],
  );

  // Filter form steps based on permissions
  const visibleFormSteps = useMemo(() => {
    if (isLoadingStepVisibility) {
      return formSteps; // Show all steps while loading
    }

    return formSteps.filter(step =>
      shouldShowStepInSidebarNav(step.id, {
        stepVisibility,
        enabledStepIds: formSteps.map(s => s.id),
        storedStepIds: formData.enabledStepIds ?? null,
      }),
    );
  }, [
    stepVisibility,
    isLoadingStepVisibility,
    formSteps,
    formData.enabledStepIds,
  ]);

  const getCurrentStepComponent = () => {
    const step = formSteps.find(s => s.id === stepId);
    return step?.component || null;
  };

  /**
   * Wizard footer navigation, derived once instead of six inline `findIndex` calls.
   *
   * `findIndex` returns **-1** for a step outside the registry (Deliverables), and
   * `handleStepNavigation` clamps both directions onto index 0 from there — so on that
   * section both arrows silently jumped to Opportunity. `isOffRegistryStep` disables the
   * pair instead; the sidebar is how that section is entered and left.
   */
  const currentVisibleStepIndex = visibleFormSteps.findIndex(
    s => s.id === stepId,
  );
  const isOffRegistryStep = currentVisibleStepIndex === -1;
  const navBlocked =
    isManualSaving || recordingState !== 'idle' || inlineImageUploadsInFlight;
  const backNavDisabled =
    isOffRegistryStep || currentVisibleStepIndex === 0 || navBlocked;
  const nextNavDisabled =
    isOffRegistryStep ||
    currentVisibleStepIndex === visibleFormSteps.length - 1 ||
    navBlocked;

  const handleStepNavigation = (direction: 'prev' | 'next') => {
    const currentIndex = visibleFormSteps.findIndex(s => s.id === stepId);
    // -1 means the current step is outside the registry (Deliverables). Both `Math.max`
    // and `Math.min` below turn -1 into 0, so without this the arrows land on Opportunity
    // whichever way they are pressed. The footer already disables them there; this is the
    // guard that makes that assertable and survives a future caller.
    if (currentIndex === -1) return;
    let newIndex;

    if (direction === 'prev') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(visibleFormSteps.length - 1, currentIndex + 1);
    }

    const newStep = visibleFormSteps[newIndex];
    if (onStepChange && newStep) {
      onStepChange(newStep.id);
    }
  };

  // sidebarWidth is now handled by App.tsx layout

  const createToolbarRawStage =
    (typeof formData.stage === 'string' && formData.stage) ||
    (typeof formData.sysRequestStage === 'string' &&
      formData.sysRequestStage) ||
    null;
  const requestToolbarStage =
    createToolbarRawStage === 'PROCESSING'
      ? null
      : normalizeStage(
          String(formData.stage || formData.sysRequestStage || ''),
        ) ?? RequestStage.DRAFT;
  /**
   * The toolbar's stage badge is rendered headlessly through RequestFrameContext and has no
   * user in scope, so the viewer has to be supplied from here (PRD FR7). Keyed on the
   * viewer's `External Requestor` role behind a roles-present guard, so a cached identity
   * whose roles never loaded hides the internal-only "Configuration" label (PRD FR28).
   */
  const showInternalDetail = showsInternalStageDetail(user);
  const createToolbarFormDataRef = useRef<Record<string, unknown>>(
    formData as Record<string, unknown>,
  );
  createToolbarFormDataRef.current = formData as Record<string, unknown>;
  const requestToolbarProps = useMemo(
    () =>
      canCreateRequest && !isAuthLoading
        ? {
            title: 'New Request',
            stage: requestToolbarStage,
            rawStage: createToolbarRawStage,
            formDataRef: createToolbarFormDataRef,
            showInternalDetail,
            disabled: isManualSaving || recordingState !== 'idle',
            actionLeading: (
              <LocationTagsControl
                requestId="new-request"
                requestStage={requestToolbarStage ?? undefined}
                disabled={isManualSaving || recordingState !== 'idle'}
                variant="toolbar"
              />
            ),
            actionTrailing: (
              <DevicesViewToolbarButton
                onPress={handleToggleBrowseByLocation}
                selected={isBrowsingByLocation}
                disabled={isManualSaving || recordingState !== 'idle'}
                variant="toolbar"
              />
            ),
          }
        : null,
    [
      canCreateRequest,
      createToolbarRawStage,
      handleToggleBrowseByLocation,
      isAuthLoading,
      isBrowsingByLocation,
      isManualSaving,
      recordingState,
      requestToolbarStage,
      showInternalDetail,
    ],
  );
  useRequestFrameToolbar(requestToolbarProps);

  // Wait for auth to resolve before checking permissions — rendering
  // "Access Denied" while user is still null causes a flash of denied content.
  if (isAuthLoading) {
    return (
      <View
        style={[
          styles.container,
          { justifyContent: 'center', alignItems: 'center' },
        ]}
      >
        <ActivityIndicator size="large" color="#4a9b4a" />
      </View>
    );
  }

  // Redirect unauthorized users after all hooks
  const pdScrollPaddingHorizontal = isSmallScreen
    ? layout.pdPhase1.narrowScrollPaddingHorizontal
    : layout.pdPhase1.scrollPaddingHorizontal;

  if (!canCreateRequest) {
    return (
      <View style={[styles.container]}>
        <SubHeader title="Access Denied" navigate={navigate} />

        <View style={styles.accessDeniedContainer}>
          <Text style={styles.accessDeniedTitle}>Access Denied</Text>
          <Text style={styles.accessDeniedMessage}>
            You don't have permission to create new requests.
          </Text>
          <TouchableOpacity
            style={styles.backToRequestsButton}
            onPress={() => navigate('/request')}
          >
            <Text style={styles.backToRequestsText}>Back to Requests</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <DimensionUnitsProvider
      units={formData.dimensionUnits}
      onChange={next => updateFormData('dimensionUnits', next)}
      loadDefaults
    >
    <View style={styles.container}>
      <FormKeyboardScrollProvider
        scrollRef={scrollViewRef}
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      >
        <View style={styles.mainContent}>
          <KeyboardAwareFormScrollView
            ref={scrollViewRef}
            style={styles.scrollContainer}
            contentContainerStyle={[
              styles.content,
              {
                paddingHorizontal: pdScrollPaddingHorizontal,
                paddingVertical: layout.pdPhase1.scrollPaddingVertical,
              },
            ]}
            showsVerticalScrollIndicator={false}
            bottomOffset={KEYBOARD_BOTTOM_OFFSET}
          >
            <View style={styles.formContainer}>
              <Text
                testID="wizard-step-title"
                style={{
                  position: 'absolute',
                  opacity: 0,
                  height: 0,
                  overflow: 'hidden',
                }}
                accessibilityRole="header"
              >
                {formSteps.find(s => s.id === currentStep)?.title || ''}
              </Text>
              {isBrowsingByLocation ? (
                <DevicesView
                  requestId="new-request"
                  enabledStepIds={resolveEnabledStepIds(
                    formData.enabledStepIds,
                  )}
                  unitOverrides={createDevicesUnitOverrides}
                  onUnitPress={handleDevicesUnitPress}
                  testID="pd-devices-view"
                />
              ) : (
                (() => {
                  if (stepId === DELIVERABLES_STEP_ID) {
                    // Mirrors RequestDetail's arm, over `formData` instead of an edit session:
                    // an unsaved request's deliverables are form state, so they die with the
                    // form and are written at first save (FR13). Never read-only here — the
                    // whole Create Request form is editable and Create Request only ever holds
                    // a Draft.
                    return (
                      /*
                       * No `readOnly` gate here, deliberately. Only `Requestor` and
                       * `External Requestor` hold `CREATE_REQUEST`, and those are exactly the
                       * two roles the Deliverables permission marks `W`, so anyone who can
                       * reach this screen can write them by definition. Gating it would only
                       * flash the section read-only while the async permission check
                       * resolves, on a form the user has just started. RequestDetail is
                       * different and IS gated: there the viewer is often not the creator.
                       */
                      <RequestDeliverablesStep
                        deliverables={formDeliverables}
                        onAdd={handleDeliverablesAdd}
                        onEdit={handleDeliverablesEdit}
                        onRemove={handleDeliverablesRemove}
                      />
                    );
                  }
                  const StepComponent = getCurrentStepComponent();
                  if (StepComponent) {
                    const stepProps: any = {
                      formData,
                      updateFormData,
                      selectedFiles,
                      onFilesSelected: handleFilesSelected,
                      offlineMode: shouldDisableUploads,
                      disabled: isManualSaving,
                      requestId: formData.id,
                      requestStage:
                        formData.stage || formData.sysRequestStage || 'DRAFT',
                      /** Requestor basic view — empty editable fields on Create Request. */
                      showFullDetails: false,
                      navScopeSnapshot,
                      enabledStepIds: resolveEnabledStepIds(
                        formData.enabledStepIds,
                      ),
                    };

                    if (stepId === 'opportunity') {
                      stepProps.validationErrors = validationErrors;
                      stepProps.registerOpportunityFormFlush =
                        registerOpportunityFormFlush;
                      if (rhfValidationRevision > 0) {
                        stepProps.rhfValidationRevision = rhfValidationRevision;
                      }
                    }
                    if (createDraftSyncRevision > 0) {
                      stepProps.parentSyncRevision = createDraftSyncRevision;
                    }
                    if (stepId === 'rack-power') {
                      stepProps.registerRackSaveHandle = registerRackSaveHandle;
                      stepProps.onRackEditorActiveChange =
                        handleRackEditorActiveChange;
                    }
                    if (stepId === 'facility-power') {
                      stepProps.registerInstanceSaveHandle =
                        registerInstanceSaveHandle;
                      stepProps.onInstanceEditorActiveChange =
                        handleInstanceEditorActiveChange;
                    }
                    if (stepId === 'large-ups') {
                      stepProps.registerLargeUpsSaveHandle =
                        registerLargeUpsSaveHandle;
                      stepProps.onLargeUpsEditorActiveChange =
                        handleLargeUpsEditorActiveChange;
                    }
                    if (stepId === 'power-distribution') {
                      stepProps.registerPowerDistributionSaveHandle =
                        registerPowerDistributionSaveHandle;
                      stepProps.onPowerDistributionEditorActiveChange =
                        handlePowerDistributionEditorActiveChange;
                    }
                    if (stepId === 'small-ups') {
                      stepProps.registerSmallUpsSaveHandle =
                        registerSmallUpsSaveHandle;
                      stepProps.onSmallUpsEditorActiveChange =
                        handleSmallUpsEditorActiveChange;
                    }
                    if (stepId === 'cooling-readiness') {
                      stepProps.registerCoolingSystemSaveHandle =
                        registerCoolingSystemSaveHandle;
                      stepProps.onCoolingSystemEditorActiveChange =
                        handleCoolingSystemEditorActiveChange;
                    }
                    if (stepId === 'liquid-cooling') {
                      stepProps.registerLiquidCoolingCduSaveHandle =
                        registerLiquidCoolingCduSaveHandle;
                      stepProps.onLiquidCoolingCduEditorActiveChange =
                        handleLiquidCoolingCduEditorActiveChange;
                    }

                    return (
                      <RequestStageProvider
                        requestStage={
                          formData.stage || formData.sysRequestStage || 'DRAFT'
                        }
                        requestId={resolveCreateRequestStageRequestId(
                          formData.id,
                        )}
                      >
                        <StepFirstSubsectionExpandProvider key={stepId}>
                          <StepComponent {...stepProps} />
                        </StepFirstSubsectionExpandProvider>
                      </RequestStageProvider>
                    );
                  }
                  return (
                    <View style={styles.placeholderContainer}>
                      <Text style={styles.placeholderTitle}>
                        {formSteps.find(s => s.id === stepId)?.title || 'Step'}
                      </Text>
                      <Text style={styles.placeholderText}>
                        This step component is not yet implemented
                      </Text>
                    </View>
                  );
                })()
              )}
            </View>
          </KeyboardAwareFormScrollView>

          {/* Upload Progress Bar */}
          {uploadProgress && (
            <UploadProgressBar
              progress={uploadProgress.progress}
              fileName={uploadProgress.fileName}
            />
          )}

          {/* Audio Recording Bar - WhatsApp-style controls shown during recording or paused */}
          <AudioRecordingBar
            recordingState={recordingState}
            elapsedTime={elapsedTime}
            visible={recordingState !== 'idle'}
            onPause={onPauseRecording}
            onResume={onResumeRecording}
            onDiscard={onDiscardRecording}
            onAccept={handleAcceptRecordingWithUpload}
          />

          {/* Sticky Footer — in-flow on native; safe inset is part of footer band */}
          <View
            style={[
              styles.footer,
              nativeFooterPaddingBottom != null && {
                paddingBottom: nativeFooterPaddingBottom,
              },
            ]}
            testID="create-request-footer"
          >
            <View
              style={[
                styles.footerContent,
                {
                  justifyContent: isSmallScreen
                    ? 'space-between'
                    : 'space-between',
                },
              ]}
            >
              {/* Left side: Recording button and Navigation buttons */}
              <View style={styles.leftButtons}>
                {/* #1149 Issue 9 — compact: the intake tools collapse behind
                    one control. Same non-wrapping-row defect as Request
                    Detail, with larger 48pt buttons; it simply has fewer
                    children today. */}
                {isCompactFooter && recordingState === 'idle' && (
                  <View style={{ marginRight: 12 }}>
                    <RequestToolsMenu
                      items={footerToolItems}
                      size={48}
                      disabled={isManualSaving}
                      testID="create-tools-menu"
                    />
                  </View>
                )}

                {/* Audio Recording Button - shown when idle, disabled when offline */}
                {!isCompactFooter && recordingState === 'idle' && (
                  <AudioRecordingButton
                    onPress={handleRecordingButtonPress}
                    disabled={isManualSaving || !isOnline}
                    style={{ marginRight: 12 }}
                  />
                )}

                {/* Paste text button - same modality row as the mic (Story 7.73) */}
                {!isCompactFooter &&
                  recordingState === 'idle' &&
                  draftButtonPermissions.canShowTextIntake && (
                    <PasteTextButton
                      testID="create-paste-text-button"
                      onPress={() => setTextIntakeModalVisible(true)}
                      disabled={isManualSaving || !isOnline}
                      style={{ marginRight: 12 }}
                    />
                  )}

                {/* Navigation Buttons */}
                <View style={styles.navigationButtons}>
                  <TouchableOpacity
                    testID="wizard-back-button"
                    style={[
                      styles.navBackButton,
                      backNavDisabled && styles.navButtonDisabled,
                    ]}
                    onPress={() => {
                      // Same -1 guard as `handleStepNavigation`: an off-registry step must
                      // not fall through to "leave the wizard".
                      if (isOffRegistryStep) return;
                      if (currentVisibleStepIndex > 0) {
                        handleStepNavigation('prev');
                      } else {
                        navigate('/request');
                      }
                    }}
                    disabled={backNavDisabled}
                  >
                    <ChevronLeft
                      size={18}
                      color={backNavDisabled ? '#ccc' : '#666'}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="wizard-next-button"
                    style={[
                      styles.navContinueButton,
                      nextNavDisabled && styles.navButtonDisabled,
                    ]}
                    onPress={() => handleStepNavigation('next')}
                    disabled={nextNavDisabled}
                  >
                    <ChevronRight
                      size={18}
                      color={nextNavDisabled ? '#ccc' : '#666'}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Action Buttons */}
              {canCreateRequest && (
                <View style={styles.actionButtons}>
                  <TouchableOpacity
                    testID="wizard-save-button"
                    style={[
                      styles.submitButton,
                      (createRequestMutation.isPending ||
                        isManualSaving ||
                        recordingState !== 'idle' ||
                        inlineImageUploadsInFlight) &&
                        styles.submitButtonDisabled,
                    ]}
                    onPress={handleFooterSave}
                    disabled={
                      createRequestMutation.isPending ||
                      isManualSaving ||
                      recordingState !== 'idle' ||
                      inlineImageUploadsInFlight
                    }
                  >
                    {createRequestMutation.isPending || isManualSaving ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={styles.submitButtonText}>
                        {footerSaveLabel}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Text intake (Paste text) modal */}
        <TextIntakeModal
          visible={textIntakeModalVisible}
          onClose={() => setTextIntakeModalVisible(false)}
          onSubmit={handleTextIntakeSubmit}
          isSubmitting={textIntakeSubmitting}
          submitLabel="Create Request"
        />

        {/* Recording Warning Modal */}
        <RecordingWarningModal
          visible={showRecordingWarning}
          onConfirm={handleRecordingWarningConfirm}
          onCancel={handleRecordingWarningCancel}
        />

        {confirmationDialog}

        <KeyboardDismissToolbar />
      </FormKeyboardScrollProvider>
    </View>
    </DimensionUnitsProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surfaceRequestCanvas,
    ...Platform.select({
      web: {
        overflow: 'hidden' as any,
      },
    }),
  },
  mainContent: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    ...Platform.select({
      web: {
        overflow: 'hidden' as any, // Prevent main content from scrolling
      },
    }),
  },
  scrollContainer: {
    flex: 1,
  },
  content: {
    alignItems: isWeb ? 'center' : 'stretch',
    ...Platform.select({
      web: {
        flexGrow: 1,
        paddingBottom: 80,
      },
    }),
  },
  formContainer: {
    width: '100%',
    maxWidth: isWeb ? spacing.formMaxWidth : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
  },
  selectSection: {
    marginBottom: spacing.belowUomRow,
  },
  selectLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  placeholderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  placeholderTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: 16,
    textAlign: 'center',
  },
  placeholderText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  footer: {
    backgroundColor: '#fff',
    paddingHorizontal: 24,
    flexShrink: 0,
    ...Platform.select({
      web: {
        paddingTop: REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING,
        paddingBottom: REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING,
        height: 'auto',
        minHeight: 0,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: -2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: -2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  footerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: isWeb ? spacing.formMaxWidth : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
    width: '100%',
    ...Platform.select({
      web: {
        minHeight: 0,
        height: 'auto',
      },
      default: {
        minHeight: REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT,
      },
    }),
    ...Platform.select({
      android: {
        paddingHorizontal: 8,
      },
      ios: {
        paddingHorizontal: 4,
      },
    }),
  },
  leftButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  navigationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
  },
  backButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  rightButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-end',
    ...Platform.select({
      android: {
        marginLeft: 8,
      },
      default: {
        gap: 12,
      },
    }),
  },
  continueButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
  },
  continueButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    marginLeft: Platform.OS === 'android' ? 6 : 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#ff6b35',
    borderRadius: 4,
  },
  submitButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    textAlign: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: '#ff6b35',
    opacity: 0.5,
  },
  syncStatus: {
    marginHorizontal: 20,
    marginTop: 10,
  },
  navigationContainer: {
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  navigationContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: isWeb ? spacing.formMaxWidth : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
    width: '100%',
  },
  navBackButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  navBackButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  navContinueButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  navContinueButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  navButtonDisabled: {
    backgroundColor: '#f0f0f0',
    borderColor: '#e0e0e0',
    opacity: 0.6,
  },
  accessDeniedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  accessDeniedTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#dc3545',
    marginBottom: 16,
    textAlign: 'center',
  },
  accessDeniedMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 22,
    maxWidth: 400,
  },
  backToRequestsButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 6,
    backgroundColor: '#ff6b35',
  },
  backToRequestsText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '500',
  },
});
